// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/AssetViewerPane.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CommentPanel.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CommentSectionBody.test.tsx
import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { type CitationCellAnchor } from "@mg/schemas/src/commons/ticket";
import {
  ArrowClockwise,
  ArrowLeft,
  ArrowSquareOut,
  ArrowsInSimple,
  ChatCircle,
  CheckCircle,
  CornersOut,
  Lock,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  Pause,
  Play,
  Plus,
  SpeakerSimpleHigh,
  SpeakerSimpleSlash,
  Warning,
  XCircle,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Flex,
  IconButton,
  Slider,
  Spinner,
  Text,
  TextArea,
  Tooltip,
} from "@radix-ui/themes";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Hls from "hls.js";
import { getDocument, type RenderTask } from "pdfjs-dist";
import {
  createContext,
  forwardRef,
  memo,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  type CommentBbox,
  computeZoomToFitBbox,
  getActiveCommentDisplayedBbox,
} from "./bboxUtils";
import { StillProcessingFallback } from "./canvas/blocks/StillProcessingFallback";
import {
  ChevronNavButton,
  chevronNavButtonClassName,
} from "./ChevronNavButton";
import { CommentCitationLinks } from "./CommentCitationLinks";
import { CommentComposeBubble } from "./CommentComposeBubble";
import { CommentSourceAttachment } from "./CommentSourceAttachment";
import { ImageCommentPin } from "./ImageCommentPin";
import {
  PANE_HEADER_HEIGHT_CLASS,
  PANE_HEADER_ICON_SIZE,
  PANE_HEADER_SIDE_INSET_CLASS,
  PANE_HEADER_SURFACE_CLASS,
  PaneHeaderIconButton,
} from "./PaneHeader";
import {
  blitPdfPageBitmap,
  PdfPageBitmapCache,
  snapshotPdfPageBitmap,
} from "./pdfPageBitmapCache";
import { resolvePdfRenderScale, type PdfRenderQuality } from "./pdfRenderScale";
import {
  type Rotation,
  inverseRotatePoint,
  nextRotation,
  rotatePoint,
  toCssPercent,
} from "./rotationUtils";
import { SpotlightOverlay } from "./SpotlightOverlay";

import { Markdown } from "../../../components/Markdown";
import { SpreadsheetView } from "../../../components/SpreadsheetView";
import { type LinkableClaim } from "../../../plugins/rehype-claim-citation";
import {
  getSignedPdfUrl,
  getSignedPdfUrlResult,
} from "../../../services/files";
import { createComment } from "../../../services/projects";
import { useAnalytics } from "../../../utils/analytics";
import { isMeaningfulGigsRole } from "../../../utils/auth";
import {
  renderCommentAvatar,
  renderReviewCommentAvatar,
} from "../../../utils/helpers/ai-persona-avatars";
import { useAppSelector } from "../../../utils/hooks";
import { downloadAsset } from "../../../utils/http";
import { ensurePdfjsWorker } from "../../../utils/pdfjsWorker";
import { useEditCommentMutation } from "../../../utils/queries/projects";
import { toTitleCase } from "../../../utils/titleCase";
import { formatTimeStamp } from "../../tickets/components/VideoPlayback";
import { useSessionPinOverride } from "../hooks/useSessionPinOverride";
import { applyActiveSessionPin } from "../utils/applyActiveSessionPin";
import { commentSectionBodyOverflows } from "../utils/commentSectionOverflow";
import {
  isKnowledgeDbCitation,
  type CitationTarget,
  type TicketMessageCitation,
} from "../utils/commentSourceUtils";
import { isPositivePageNum } from "../utils/pageNum";
import {
  extractRiskSection,
  commentSectionMarkdown,
  formatStructuredCommentMarkdown,
  parseCommentSections,
  slugifyCommentSectionLabel,
  type CommentSection,
} from "../utils/parseCommentSections";
import {
  rejectedReviewCommentCount,
  reviewCommentsForPanel,
} from "../utils/visibleComments";

ensurePdfjsWorker();

export type { CitationTarget };

export interface AssetFile {
  source: string;
  name?: string;
  contentType?: string;
  /** Optional low-res preview for blur-up while the full source loads. */
  thumbnailUrl?: string;
}

/**
 * Zoom bounds shared by `PdfViewer` and `ImageViewer`. Hoisted to file
 * scope so wheel-zoom, keyboard +/-, and the explicit zoom buttons can
 * never drift apart — every cap funnels through `clampZoom`.
 *
 * Why 10x (1000%) as the ceiling:
 *   - Packshot reviewers (brand + regulatory) need to inspect sub-pixel
 *     artifacts on small back-panel text and barcode swatches; 300% (and
 *     even the interim 600% from #3461) was not enough for that use case
 *     and people complained.
 *   - Layout zoom is CSS (`wrapper width = zoom * 100%`). pdf.js re-rasters
 *     only after zoom settles, at a pixel-budgeted scale — so 10x stays
 *     usable even on large-format pages that cannot allocate a 15x canvas.
 *
 * If a reviewer asks for higher: bump `MAX_ZOOM` only — every code path
 * already reads from it.
 */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 10;
const ZOOM_STEP = 0.25;
/** Wait for trackpad/keyboard zoom to idle before a settled pdf.js re-raster. */
const PDF_ZOOM_SETTLE_MS = 180;

/** Phosphor ChatCircle paths (@phosphor-icons/react, 256 viewBox). */
const PHOSPHOR_CHAT_CIRCLE_FILL =
  "M232,128A104,104,0,0,1,79.12,219.82L45.07,231.17a16,16,0,0,1-20.24-20.24l11.35-34.05A104,104,0,1,1,232,128Z";
const PHOSPHOR_CHAT_CIRCLE_REGULAR =
  "M128,24A104,104,0,0,0,36.18,176.88L24.83,210.93a16,16,0,0,0,20.24,20.24l34.05-11.35A104,104,0,1,0,128,24Zm0,192a87.87,87.87,0,0,1-44.06-11.81,8,8,0,0,0-6.54-.67L40,216,52.47,178.6a8,8,0,0,0-.66-6.54A88,88,0,1,1,128,216Z";
/** Phosphor Plus (regular) — centered in the placement cursor bubble. */
const PHOSPHOR_PLUS_REGULAR =
  "M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z";
/** puntt-accent-9 / puntt-blue-9 — matches CommentPanel ChatCircle icon color. */
const COMMENT_MODE_CURSOR_ICON_COLOR = "#3c39ec";

/**
 * Placement cursor: ChatCircle + plus (same affordance as comment-panel-add),
 * with an opaque white fill behind the ring so the bubble interior is never
 * transparent.
 */
const COMMENT_MODE_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 256 256">' +
    `<path fill="#ffffff" d="${PHOSPHOR_CHAT_CIRCLE_FILL}"/>` +
    `<path fill="${COMMENT_MODE_CURSOR_ICON_COLOR}" d="${PHOSPHOR_CHAT_CIRCLE_REGULAR}"/>` +
    `<path fill="${COMMENT_MODE_CURSOR_ICON_COLOR}" transform="translate(80 80) scale(0.375)" d="${PHOSPHOR_PLUS_REGULAR}"/>` +
    "</svg>",
)}") 8 28, crosshair`;

/** Red circle cursor for context-asset source placement. */
const SOURCE_PLACEMENT_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">' +
    '<circle cx="12" cy="12" r="9" fill="#ffffff"/>' +
    '<circle cx="12" cy="12" r="7" fill="#e5484d"/>' +
    "</svg>",
)}") 12 12, crosshair`;

export type ContextSourcePinPayload = {
  relX: number;
  relY: number;
  page: number;
  sourceUrl: string;
};

/**
 * Human-readable label for a citation into a spreadsheet source, e.g.
 * "Claims B7", "Claims row 42", or "Sheet 2". Exported for tests.
 */
export function citationCellAnchorLabel(anchor: CitationCellAnchor): string {
  const sheet =
    anchor.sheetName?.trim() ||
    (anchor.sheetIndex != null ? `Sheet ${anchor.sheetIndex + 1}` : "Sheet");
  if (anchor.range) return `${sheet} ${anchor.range}`;
  if (anchor.row != null) return `${sheet} row ${anchor.row}`;
  return sheet;
}

function commentPanelHeaderLabel(
  comment: BaseTicketComment,
  currentUserName: string | null | undefined,
  fallbackLabel: string,
): string {
  if (comment.isAI) return toTitleCase(comment.rule ?? fallbackLabel);
  if (
    typeof comment.createdBy !== "string" &&
    comment.createdBy?.name?.trim()
  ) {
    return comment.createdBy.name.trim();
  }
  return currentUserName?.trim() || fallbackLabel;
}

function withHumanCommentAuthor(
  comment: BaseTicketComment,
  user: { userID?: string; name?: string; avatar?: string } | null,
  fallbackName: string,
): BaseTicketComment {
  if (comment.isAI || user == null) return comment;
  if (
    typeof comment.createdBy !== "string" &&
    comment.createdBy?.name?.trim()
  ) {
    return comment;
  }
  return {
    ...comment,
    isAI: false,
    createdBy: {
      _id: user.userID ?? "",
      name: user.name ?? fallbackName,
      avatar: user.avatar,
    },
  };
}

export type DraftPin = { relX: number; relY: number; page: number };

/** What the pane hands to a caller-supplied human-comment sink. */
export type HumanCommentSubmitInput = {
  description: string;
  relX: number;
  relY: number;
  page: number;
  citations?: TicketMessageCitation[];
};

export type HumanCommentPlacementTrigger = "toggle" | "click";

export type CommentComposePlacement = "panel" | "inline";

/** Props surfaced when the docked comment panel is rendered outside the viewer. */
export type DetachedCommentPanelProps = {
  comments: BaseTicketComment[];
  allReviewComments?: BaseTicketComment[];
  includeRejectedComments?: boolean;
  onIncludeRejectedCommentsChange?: (include: boolean) => void;
  activeCommentId?: string | null;
  onCommentClick?: (comment: BaseTicketComment) => void;
  onCitationClick?: (citation: CitationTarget) => void;
  onNavigateToPage?: (page: number) => void;
  ticketId?: string;
  commentMode: boolean;
  draftPin: DraftPin | null;
  onToggleCommentMode: () => void;
  onSubmitComment: (
    description: string,
    citations?: TicketMessageCitation[],
  ) => void | Promise<void>;
  canvasPinPlacementEnabled: boolean;
  isSubmitting: boolean;
  currentUserName?: string | null;
  currentUserAvatar?: string;
  draftCitations: TicketMessageCitation[];
  onDraftCitationsChange: (citations: TicketMessageCitation[]) => void;
  hasContextAssets: boolean;
  onStartContextSourcePlacement?: (
    onPinComplete?: (citation: TicketMessageCitation) => void,
  ) => void;
  sourcePlacementActive?: boolean;
};

type InlineComposeViewerProps = {
  commentComposePlacement: "inline";
  composeText: string;
  onComposeTextChange: (value: string) => void;
  onSubmitInlineComment: () => void | Promise<void>;
  onCancelCompose: () => void;
  isSubmitting: boolean;
  submitError: string | null;
  authorName: string;
  authorAvatar?: string;
};

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function relCoordsFromViewerClick(
  clientX: number,
  clientY: number,
  wrapperEl: HTMLDivElement,
  rotation: Rotation,
): { relX: number; relY: number } | null {
  const rect = wrapperEl.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  const displayX = clamp01((clientX - rect.left) / rect.width);
  const displayY = clamp01((clientY - rect.top) / rect.height);
  const natural = inverseRotatePoint({ x: displayX, y: displayY }, rotation);
  return { relX: clamp01(natural.x), relY: clamp01(natural.y) };
}

/** Clamp a zoom level to the shared `[MIN_ZOOM, MAX_ZOOM]` interval. */
function clampZoom(z: number): number {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
}

// Track which orphan-page comments we've already warned about so a single
// missing-page_num doesn't spam the console as the comments list re-polls.
const orphanPageWarnedIds = new Set<string>();

/**
 * Comment pins rendered on the asset: when a comment is selected, only that
 * pin is shown so stepping through the carousel reveals one finding at a time.
 */
function canvasCommentsForPage(
  comments: BaseTicketComment[] | undefined,
  activeCommentId: string | null | undefined,
  pageNum: number,
): BaseTicketComment[] {
  const onPage = (comments ?? []).filter((c) => (c.page_num ?? 1) === pageNum);
  if (!activeCommentId) return onPage;
  return onPage.filter((c) => c._id === activeCommentId);
}

function useOrphanPagePinWarning(comments?: BaseTicketComment[]) {
  useEffect(() => {
    if (!comments?.length) return;
    for (const c of comments) {
      const id = c?._id != null ? String(c._id) : null;
      if (!id) continue;
      if (c.page_num != null) continue;
      if (orphanPageWarnedIds.has(id)) continue;
      orphanPageWarnedIds.add(id);
      // Renderer falls back to page 1, but that's a backend bug — the
      // pin will land on the wrong page silently. Log so we can spot it
      // in dev/Datadog instead of waiting for a customer to report.
      console.warn(
        "[AskPuntt] Comment rendered with no page_num; falling back to page 1.",
        { commentId: id },
      );
    }
  }, [comments]);
}

/**
 * Target location inside the viewer for the transient highlight ping.
 * Image/PDF targets use normalized x/y (+ page); video targets use
 * `videoStart` (seconds, seeks the player); spreadsheet targets use
 * `cellAnchor` (selects the sheet and outlines the cited cells/row).
 */
export interface HighlightPosition {
  x?: number;
  y?: number;
  page?: number;
  videoStart?: number;
  cellAnchor?: CitationCellAnchor;
}

interface AssetViewerPaneProps {
  title: string;
  assets: AssetFile[];
  highlightPosition?: HighlightPosition | null;
  reviewStatus?: string | null;
  comments?: BaseTicketComment[];
  /** Full comment list (including rejected) for the panel carousel. */
  allReviewComments?: BaseTicketComment[];
  includeRejectedComments?: boolean;
  onIncludeRejectedCommentsChange?: (include: boolean) => void;
  activeCommentId?: string | null;
  onCommentClick?: (comment: BaseTicketComment) => void;
  onPinClick?: (comment: BaseTicketComment) => void;
  onCitationClick?: (citation: CitationTarget) => void;
  onPinDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
  /** Fires on a bare artwork click (not a pin, not a pan gesture). */
  onArtworkBackgroundClick?: () => void;
  onCollapse?: () => void;
  onBackToProject?: () => void;
  ticketId?: string;
  onPasswordRequired?: (fileName: string) => void;
  /**
   * When true, the internal bottom comment panel is not rendered. Pins still
   * draw on the asset (driven by `comments`) — only the docked carousel is
   * suppressed. The project review screen uses this so comments live in a
   * standalone bubble beside the artwork instead of under it.
   */
  hideCommentPanel?: boolean;
  /** Suppress the uppercase title row when a parent already shows the review name. */
  hideTitleRow?: boolean;
  /**
   * `bottom` (default): page nav + zoom/rotate toolbar under the asset.
   * `external`: toolbar is reported via `onHeaderControlsChange` for mounting
   * in a parent header row (project review artwork/context columns).
   * `header`: toolbar is hosted in this pane's OWN header row (next to the
   * title + collapse button) instead of a footer (chat artwork/context panes).
   */
  viewerToolbarPlacement?: "bottom" | "external" | "header";
  /**
   * `default`: inset the scrollable viewer canvas with padding.
   * `edge`: flush to the column width.
   * `inset16`: 16px inset so the gray viewer chrome shows around the asset.
   */
  viewerContentInset?: "default" | "edge" | "inset16";
  onHeaderControlsChange?: (controls: ReactNode | null) => void;
  /**
   * Where human comment composition renders. `panel` (default): bottom
   * CommentPanel card. `inline`: popup at the draft pin on the artwork
   * (Ask Puntt Projects review).
   */
  commentComposePlacement?: "panel" | "inline";
  /**
   * Where a saved human comment goes. Default: the ticket comment endpoint
   * (`ticketId` required). Ask Puntt V2 reviews have no ticket, so the review
   * workspace supplies a sink that posts to the review document instead.
   */
  onSubmitHumanComment?: (input: HumanCommentSubmitInput) => Promise<void>;
  /**
   * `toggle` (default): the user arms comment mode from the panel button,
   * then clicks the artwork. `click`: a bare artwork click (not a pin, not a
   * pan) drops the draft pin and opens the inline compose bubble directly.
   */
  humanCommentPlacementTrigger?: HumanCommentPlacementTrigger;
  /**
   * When set alongside `hideCommentPanel`, the viewer reports the props
   * needed to render `CommentPanel` in a parent column (e.g. the chat
   * review context pane) instead of docking it below the asset.
   */
  onDetachedCommentPanelProps?: (
    props: DetachedCommentPanelProps | null,
  ) => void;
  /** Controlled draft sources while composing a human comment (MG). */
  draftCitations?: TicketMessageCitation[];
  onDraftCitationsChange?: (citations: TicketMessageCitation[]) => void;
  hasContextAssets?: boolean;
  onStartContextSourcePlacement?: (
    onPinComplete?: (citation: TicketMessageCitation) => void,
  ) => void;
  sourcePlacementActive?: boolean;
  /** Context pane: temporary pin when navigating to a cited position. */
  citationFocusPin?: DraftPin | null;
  /** Context pane: click-to-pin source placement mode. */
  sourcePlacementMode?: boolean;
  draftSourcePin?: DraftPin | null;
  onPlaceSourcePin?: (pin: ContextSourcePinPayload) => void;
  onCancelSourcePlacement?: () => void;
  /** Review workspace: map comment ids to their 1-based list index for numbered pins. */
  commentNumberById?: Record<string, number>;
  /** Embedded citation previews: open this asset tab on first mount. */
  initialAssetIndex?: number;
  /** Embedded citation previews: open this page on the active asset viewer. */
  initialViewerPage?: number;
}

type ViewerToolbarPlacement = "bottom" | "external" | "header";
type ViewerContentInset = "default" | "edge" | "inset16";

const ViewerContentInsetContext = createContext<ViewerContentInset>("default");

function viewerScrollPadding(inset: ViewerContentInset): string {
  if (inset === "edge") return "p-0";
  if (inset === "inset16") return "p-4";
  return "p-2";
}

const ViewerToolbarPlacementContext = createContext<{
  placement: ViewerToolbarPlacement;
  setHeaderControls: (controls: ReactNode | null) => void;
} | null>(null);

function useReportedViewerToolbar(controls: ReactNode | null, deps: unknown[]) {
  const ctx = useContext(ViewerToolbarPlacementContext);
  // Read the toolbar node through a ref rather than depending on its identity.
  // The cluster is rebuilt every render (its memo closes over non-memoized
  // zoom/rotate handlers), so depending on the node would re-run this effect
  // every render. In `header` placement the report writes back into THIS
  // pane's own state, which re-renders the inner viewer and rebuilds the node
  // again — an infinite commit loop. `external` placement dodged it only
  // because the report landed in a memoized parent. Keying the effect on the
  // caller-supplied primitive deps (page/zoom/rotation) keeps both safe.
  const controlsRef = useRef(controls);
  controlsRef.current = controls;
  useEffect(() => {
    // Report the cluster for any non-bottom placement: `external` mounts it in
    // a parent header, `header` mounts it in this pane's own header.
    if (!ctx || ctx.placement === "bottom") return;
    ctx.setHeaderControls(controlsRef.current);
    return () => ctx.setHeaderControls(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller supplies the meaningful deps; node read via ref to avoid identity churn
  }, [ctx, ...deps]);
}

export interface AssetViewerPaneHandle {
  navigateToPage: (page: number) => void;
  // `pendingPage` lands on the newly-activated file once its viewer mounts,
  // even before the document has finished loading. Required because
  // navigateToFile's setActiveTab is deferred; a synchronous navigateToPage
  // fired right after it would hit the outgoing viewer's setter.
  navigateToFile: (fileIndex: number, pendingPage?: number) => void;
}

function isPdf(asset: AssetFile): boolean {
  if (asset.contentType?.includes("pdf")) return true;
  const name = (asset.name ?? "").toLowerCase();
  const source = (asset.source ?? "").toLowerCase();
  // why: display name stays the office original after samba conversion; the PDF is on source
  return /\.(pdf|ai)(\?|$)/.test(name) || /\.(pdf|ai)(\?|$)/.test(source);
}

function isImage(asset: AssetFile): boolean {
  if (asset.contentType?.startsWith("image/")) return true;
  const ext = (asset.name ?? asset.source).toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/.test(ext);
}

// Spreadsheet files are rendered client-side by SpreadsheetView (xlsx parsed
// in the browser into a real table with cell-anchored comment overlays). They
// are NOT routed through PdfViewer — pdf.js cannot parse raw xlsx bytes and
// would surface "Failed to load PDF". See SpreadsheetViewer below.
function isSpreadsheet(asset: AssetFile): boolean {
  const ext = (asset.name ?? asset.source).toLowerCase();
  if (/\.(xls|xlsx|csv|ods)(\?|$)/.test(ext)) return true;
  const ct = asset.contentType ?? "";
  return (
    ct.includes("spreadsheetml") || ct.includes("ms-excel") || ct === "text/csv"
  );
}

/**
 * Raw Word/PowerPoint bytes that samba's LibreOffice conversion has not turned
 * into a PDF yet. These reach the pane occasionally (context files especially)
 * and must not fall through to the ImageViewer fallback — an `<img>` pointed at
 * a .docx renders as a silent broken image. They must NEVER be routed to
 * view.officeapps.live.com either (no pins, no citations — see the AGENT
 * CONTRACT on supportsCanvasPinPlacement).
 */
function isUnconvertedOffice(asset: AssetFile): boolean {
  // Samba may serve converted PDF bytes while the asset still carries a .docx
  // name — isPdf runs first in the render chain, but pin placement calls this
  // helper directly and must not treat converted Office as unconverted.
  if (isPdf(asset)) return false;
  const ext = (asset.name ?? asset.source).toLowerCase();
  if (/\.(doc|docx|ppt|pptx)(\?|$)/.test(ext)) return true;
  const ct = asset.contentType ?? "";
  return (
    ct.includes("wordprocessingml") ||
    ct.includes("presentationml") ||
    ct === "application/msword" ||
    ct === "application/vnd.ms-powerpoint"
  );
}

/**
 * Text uploads (txt/rtf/md/markdown/json/xml/yaml) are rendered server-side
 * into a paginated PDF, so they have real pages to review, thumbnails and
 * comment pins like any other deliverable. Samba rewrites the file source to
 * that render, so the SOURCE — not the display name, which still carries the
 * original .txt — is what says a render exists.
 *
 * Without this branch a rendered text asset falls through to the ImageViewer
 * fallback and paints a silent broken `<img>` for a perfectly reviewable
 * deliverable (`isPdf` reads `asset.name ?? asset.source`, and the name is
 * still "spec.txt").
 */
function isRenderedText(asset: AssetFile): boolean {
  if (isVideo(asset) || isSpreadsheet(asset) || isImage(asset)) return false;
  const name = (asset.name ?? "").toLowerCase();
  const source = (asset.source ?? "").toLowerCase();
  const isTextUpload =
    /\.(txt|rtf|md|markdown|json|xml|yaml|yml)(\?|$)/.test(name) ||
    /\.(txt|rtf|md|markdown|json|xml|yaml|yml)(\?|$)/.test(source);
  return isTextUpload && /\.pdf(\?|$)/.test(source);
}

function isVideo(asset: AssetFile): boolean {
  if (asset.contentType?.startsWith("video/")) return true;
  if (
    asset.contentType === "application/vnd.apple.mpegurl" ||
    asset.contentType === "application/x-mpegurl"
  )
    return true;
  const ext = (asset.name ?? asset.source).toLowerCase();
  return /\.(mp4|mov|avi|wmv|flv|mkv|m3u8)(\?|$)/.test(ext);
}

/**
 * Image/PDF viewers support rel_x/rel_y pins; video and spreadsheets do not.
 * Spreadsheets anchor comments to cells (cellAnchor), not normalized canvas
 * coordinates, so canvas pin placement is disabled.
 *
 * AGENT CONTRACT (HARD): Never reintroduce OfficeViewer /
 * view.officeapps.live.com for pptx/ppt/docx/doc (or any pin-capable type).
 * That iframe has zero pin/citation support (#product 2026-08-04 / PR #4409).
 * Office files reach PdfViewer via samba LibreOffice conversion (…-context.pdf
 * / application/pdf). Spreadsheets use SpreadsheetViewer. Tests that guard
 * this must stay red if the iframe returns — do not update them to expect
 * officeapps (AssetViewerPane.test.tsx "pin-capable file types").
 */
function supportsCanvasPinPlacement(asset: AssetFile): boolean {
  return (
    !isVideo(asset) && !isSpreadsheet(asset) && !isUnconvertedOffice(asset)
  );
}

// ---------------------------------------------------------------------------
// CommentSectionBody — full-height comment display with no internal scroll.
// AI comments with 2+ bold-label paragraphs render as a vertical stack of
// inline rows (`Issue: …`, `Fix: …`); Risk renders in the comment header.
// Long section copy clamps to two lines; a single View more below the stack
// expands every category when any one of them overflows.
// Unstructured human comments fall back to single-column prose-width markdown.
// ---------------------------------------------------------------------------

const COMMENT_MARKDOWN_CLASS =
  "text-base leading-relaxed [&_p]:text-base [&_span.font-bold]:text-base [&_p.font-bold]:text-base [&_span.font-bold]:text-puntt-neutral-gray-12 [&_p.font-bold]:text-puntt-neutral-gray-12";

const COMMENT_SECTION_MARKDOWN_CLASS = `${COMMENT_MARKDOWN_CLASS} [&_p]:m-0`;

const COMMENT_SECTION_LINE_CLAMP_CLASS = "line-clamp-2";

function commentSectionRenderedText(section: CommentSection): string {
  return commentSectionMarkdown(section);
}

function CommentSectionColumn({
  section,
  sectionsExpanded,
  clampSections,
  onOverflowChange,
  claimCitations,
  claimCitationScope,
}: {
  section: CommentSection;
  sectionsExpanded: boolean;
  clampSections: boolean;
  onOverflowChange: (sectionSlug: string, overflows: boolean) => void;
  claimCitations?: LinkableClaim[];
  claimCitationScope?: string;
}) {
  const sectionSlug = slugifyCommentSectionLabel(section.label);
  const bodyRef = useRef<HTMLDivElement>(null);
  const showClamp = clampSections && !sectionsExpanded;

  useLayoutEffect(() => {
    if (!clampSections || sectionsExpanded) return;
    const el = bodyRef.current;
    if (!el) return;

    const renderedText = commentSectionRenderedText(section);
    const measure = () => {
      onOverflowChange(
        sectionSlug,
        commentSectionBodyOverflows(el, renderedText),
      );
    };

    measure();
    const raf = requestAnimationFrame(measure);
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => measure())
        : null;
    observer?.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [section, sectionsExpanded, sectionSlug, clampSections, onOverflowChange]);

  return (
    <div data-testid={`comment-section-${sectionSlug}`} className="min-w-0">
      <div
        ref={bodyRef}
        className={showClamp ? COMMENT_SECTION_LINE_CLAMP_CLASS : undefined}
        data-testid={`comment-section-${sectionSlug}-body`}
      >
        <Markdown
          size="3"
          uniformBodyTypography
          className={COMMENT_SECTION_MARKDOWN_CLASS}
          claimCitations={claimCitations}
          // Section slug as well as the comment id: one comment's sections are
          // separate bodies, and the same rule can be quoted in more than one.
          claimCitationScope={
            claimCitationScope
              ? `${claimCitationScope}-${sectionSlug}`
              : undefined
          }
        >
          {commentSectionMarkdown(section)}
        </Markdown>
      </div>
    </div>
  );
}

const COMMENT_SECTION_STACK_CLASS = "flex flex-col";

function commentRiskBadgeColor(
  body: string,
): "red" | "orange" | "yellow" | "green" | "gray" {
  const normalized = body.trim().toLowerCase();
  if (
    normalized.includes("block") ||
    normalized.includes("high") ||
    normalized.includes("critical")
  ) {
    return "red";
  }
  if (normalized.includes("medium") || normalized.includes("warn")) {
    return "orange";
  }
  if (normalized.includes("low") || normalized.includes("info")) {
    return "green";
  }
  return "gray";
}

function formatRiskHeaderValue(body: string): string {
  return body.trim().split(/\n/)[0].replace(/\*\*/g, "").trim();
}

export function CommentRiskHeaderBadge({ body }: { body: string }) {
  const { t } = useTranslation("ui");
  const value = formatRiskHeaderValue(body);
  if (!value) return null;

  return (
    <Flex
      align="center"
      gap="1"
      className="ml-auto shrink-0"
      data-testid="comment-header-risk"
    >
      <Text
        size="1"
        weight="medium"
        className="uppercase tracking-wide text-puntt-neutral-gray-9"
      >
        {t("assetViewer.riskLabel")}
      </Text>
      <Badge
        size="1"
        variant="soft"
        color={commentRiskBadgeColor(value)}
        data-testid="comment-header-risk-value"
      >
        {value}
      </Badge>
    </Flex>
  );
}

function CommentPanelKnowledgeRecordCitationLinks({
  recordIds,
  onCitationClick,
}: {
  recordIds: string[];
  onCitationClick?: (citation: CitationTarget) => void;
}) {
  const { t } = useTranslation("ui");
  if (recordIds.length === 0) return null;

  return (
    <Flex gap="2" wrap="wrap" className="mt-2 shrink-0">
      {recordIds.map((recordId, idx) => (
        <button
          key={`${recordId}-${idx}`}
          type="button"
          data-testid="comment-panel-knowledge-record-citation"
          onClick={() => onCitationClick?.({ knowledgeDataRecordId: recordId })}
          className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-puntt-accent-11 underline underline-offset-2 hover:bg-transparent hover:text-puntt-accent-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
          aria-label={t("assetViewer.viewApprovedClaimAriaLabel")}
        >
          <Text as="span" size="1" weight="medium">
            {t("assetViewer.approvedClaim")}
          </Text>
          <ArrowSquareOut size={12} aria-hidden="true" />
        </button>
      ))}
    </Flex>
  );
}

export function CommentSectionBody({
  description,
  commentId,
  slotAfterExpand,
  clampCommentSections = true,
  claimCitations,
}: {
  description: string;
  commentId: string | null;
  /** Rendered after View more / View less (e.g. source citation links). */
  slotAfterExpand?: ReactNode;
  /**
   * When false, category rows render at full height with no line-clamp or
   * View more / View less (comments-only right column with no context asset).
   */
  clampCommentSections?: boolean;
  /**
   * Registry claims this comment may QUOTE. Where the prose carries one, the
   * wording becomes the link to the rule it rests on.
   */
  claimCitations?: LinkableClaim[];
}) {
  const { t } = useTranslation("ui");
  const sections = useMemo(() => {
    const parsed = parseCommentSections(description);
    return extractRiskSection(parsed).sections;
  }, [description]);
  const useColumns = sections.length >= 2;
  const [sectionsExpanded, setSectionsExpanded] = useState(false);
  const [overflowBySection, setOverflowBySection] = useState<
    Record<string, boolean>
  >({});

  const handleSectionOverflowChange = useCallback(
    (sectionSlug: string, overflows: boolean) => {
      setOverflowBySection((prev) =>
        prev[sectionSlug] === overflows
          ? prev
          : { ...prev, [sectionSlug]: overflows },
      );
    },
    [],
  );

  const hasAnyOverflow = useMemo(
    () => Object.values(overflowBySection).some(Boolean),
    [overflowBySection],
  );

  useEffect(() => {
    setSectionsExpanded(false);
  }, [commentId, description]);

  // Drop stale overflow entries when the parsed section list changes.
  useLayoutEffect(() => {
    const activeSlugs = new Set(
      sections.map((s) => slugifyCommentSectionLabel(s.label)),
    );
    setOverflowBySection((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([slug]) => activeSlugs.has(slug)),
      );
      return Object.keys(next).length === Object.keys(prev).length
        ? prev
        : next;
    });
  }, [sections]);

  return (
    <div
      className="min-w-0 break-words text-base leading-relaxed text-puntt-neutral-gray-12 [overflow-wrap:anywhere]"
      data-testid={
        commentId ? `comment-body-${commentId}` : "comment-body-anonymous"
      }
    >
      <div data-testid="comment-panel-body">
        {useColumns ? (
          <>
            <div className={COMMENT_SECTION_STACK_CLASS}>
              {sections.map((section) => (
                <CommentSectionColumn
                  key={section.label}
                  section={section}
                  sectionsExpanded={sectionsExpanded}
                  clampSections={clampCommentSections}
                  onOverflowChange={handleSectionOverflowChange}
                  claimCitations={claimCitations}
                  claimCitationScope={commentId ?? undefined}
                />
              ))}
            </div>
            {clampCommentSections && !sectionsExpanded && hasAnyOverflow ? (
              <button
                type="button"
                data-testid="comment-sections-view-more"
                aria-label={t("assetViewer.viewMoreCommentDetailsAriaLabel")}
                className="mt-2 cursor-pointer border-0 bg-transparent p-0 text-left hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
                onClick={() => setSectionsExpanded(true)}
              >
                <Text as="span" size="1" className="text-puntt-neutral-gray-11">
                  {t("assetViewer.viewMore")}
                </Text>
              </button>
            ) : null}
            {clampCommentSections && sectionsExpanded && hasAnyOverflow ? (
              <button
                type="button"
                data-testid="comment-sections-view-less"
                aria-label={t("assetViewer.viewLessCommentDetailsAriaLabel")}
                className="mt-2 cursor-pointer border-0 bg-transparent p-0 text-left hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
                onClick={() => setSectionsExpanded(false)}
              >
                <Text as="span" size="1" className="text-puntt-neutral-gray-11">
                  {t("assetViewer.viewLess")}
                </Text>
              </button>
            ) : null}
            {slotAfterExpand}
          </>
        ) : (
          <>
            <div className="max-w-prose">
              <Markdown
                size="3"
                uniformBodyTypography
                className={COMMENT_MARKDOWN_CLASS}
                claimCitations={claimCitations}
                claimCitationScope={commentId ?? undefined}
              >
                {formatStructuredCommentMarkdown(description) ?? description}
              </Markdown>
            </div>
            {slotAfterExpand}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Draft pin preview while composing a human comment
// ---------------------------------------------------------------------------

function DraftCommentPin({
  relX,
  relY,
  rotation,
  "data-testid": dataTestId = "comment-draft-pin",
}: {
  relX: number;
  relY: number;
  rotation: Rotation;
  "data-testid"?: string;
}) {
  const rotated = rotatePoint({ x: relX, y: relY }, rotation);
  return (
    <div
      data-testid={dataTestId}
      className="pointer-events-none absolute z-20"
      style={{
        left: toCssPercent(rotated.x),
        top: toCssPercent(rotated.y),
      }}
      role="presentation"
      aria-hidden="true"
    >
      <div
        className="rounded-t-2xl rounded-br-2xl bg-puntt-gold-10 p-0.5 shadow-md ring-2 ring-puntt-accent-9 ring-offset-1"
        style={{
          transform: "translateY(-100%) rotate(-45deg) scale(1.2)",
          transformOrigin: "0% 100%",
        }}
      >
        <div
          className="flex size-6 items-center justify-center rounded-full bg-puntt-gold-10"
          style={{ transform: "rotate(45deg)" }}
        >
          <Warning size={14} weight="fill" className="text-base-white" />
        </div>
      </div>
    </div>
  );
}

function CommentFeedbackActionButton({
  "data-testid": dataTestId,
  authTrigger,
  label,
  icon: Icon,
  pressed,
  onClick,
  tone,
  disabled,
  ariaLabel,
}: {
  "data-testid": string;
  authTrigger?: string;
  label: string;
  icon: typeof CheckCircle;
  pressed: boolean;
  onClick: () => void;
  tone: "confirm" | "reject" | "restore";
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const { t } = useTranslation("ui");
  const isConfirm = tone === "confirm";
  const isRestore = tone === "restore";
  const variant = isConfirm && pressed ? "solid" : pressed ? "soft" : "outline";
  return (
    <Button
      type="button"
      variant={variant}
      size="1"
      color={isConfirm ? "green" : "gray"}
      data-testid={dataTestId}
      data-auth-trigger={authTrigger}
      aria-label={
        ariaLabel ??
        (pressed
          ? t("assetViewer.removeLabel", { label: label.toLowerCase() })
          : label)
      }
      aria-pressed={isRestore ? undefined : pressed}
      onClick={onClick}
      disabled={disabled}
      className="min-w-0 flex-1 basis-0"
    >
      <Flex as="span" gap="1" align="center" justify="center">
        <Icon
          size={14}
          weight={isConfirm && pressed ? "fill" : "regular"}
          aria-hidden="true"
        />
        {label}
      </Flex>
    </Button>
  );
}

// ---------------------------------------------------------------------------
// CommentPanel -- single-comment navigation view (replaces CommentStrip)
//
// Read-only carousel for existing comments; human comments are added via
// comment-placement mode (toggle + canvas click + compose form).
// ---------------------------------------------------------------------------

export function CommentPanel({
  comments,
  allReviewComments,
  includeRejectedComments = false,
  onIncludeRejectedCommentsChange,
  activeCommentId,
  onCommentClick,
  onCitationClick,
  onNavigateToPage,
  ticketId,
  commentMode = false,
  draftPin = null,
  onToggleCommentMode,
  onSubmitComment,
  canvasPinPlacementEnabled = true,
  isSubmitting = false,
  currentUserName,
  currentUserAvatar,
  commentComposePlacement = "panel",
  fillContainer = false,
  navTrailing,
  clampCommentSections = true,
  draftCitations = [],
  onDraftCitationsChange,
  hasContextAssets = false,
  onStartContextSourcePlacement,
  sourcePlacementActive = false,
  navigableCitations,
  activeCitationIndex = 0,
  onCommentNavigate,
}: {
  comments: BaseTicketComment[];
  allReviewComments?: BaseTicketComment[];
  includeRejectedComments?: boolean;
  onIncludeRejectedCommentsChange?: (include: boolean) => void;
  activeCommentId?: string | null;
  onCommentClick?: (comment: BaseTicketComment) => void;
  onCitationClick?: (citation: CitationTarget) => void;
  onNavigateToPage?: (page: number) => void;
  ticketId?: string;
  commentMode?: boolean;
  draftPin?: DraftPin | null;
  onToggleCommentMode?: () => void;
  onSubmitComment?: (
    description: string,
    citations?: TicketMessageCitation[],
  ) => void | Promise<void>;
  /** False on video / Office viewers where canvas pin placement is unavailable. */
  canvasPinPlacementEnabled?: boolean;
  isSubmitting?: boolean;
  currentUserName?: string | null;
  currentUserAvatar?: string;
  commentComposePlacement?: CommentComposePlacement;
  fillContainer?: boolean;
  navTrailing?: ReactNode;
  clampCommentSections?: boolean;
  draftCitations?: TicketMessageCitation[];
  onDraftCitationsChange?: (citations: TicketMessageCitation[]) => void;
  hasContextAssets?: boolean;
  onStartContextSourcePlacement?: (
    onPinComplete?: (citation: TicketMessageCitation) => void,
  ) => void;
  sourcePlacementActive?: boolean;
  navigableCitations?: TicketMessageCitation[];
  activeCitationIndex?: number;
  onCommentNavigate?: (dir: 1 | -1) => void;
}) {
  const { t } = useTranslation("ui");
  const authUser = useAppSelector((state) => state.auth.value);
  const authorName =
    currentUserName ?? authUser?.name ?? t("assetViewer.defaultAuthorName");
  const authorAvatar = currentUserAvatar ?? authUser?.avatar;

  const sourceComments = allReviewComments ?? comments;
  const displayComments = useMemo(
    () => reviewCommentsForPanel(sourceComments, includeRejectedComments),
    [sourceComments, includeRejectedComments],
  );
  const rejectedCount = useMemo(
    () => rejectedReviewCommentCount(sourceComments),
    [sourceComments],
  );

  // Track index internally so the 3-second highlight auto-clear doesn't
  // reset the panel back to comment 0.
  const [internalIndex, setInternalIndex] = useState(0);
  const [composeText, setComposeText] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!commentMode) {
      setComposeText("");
      setSubmitError(null);
    }
  }, [commentMode]);

  useEffect(() => {
    if (!activeCommentId) return;
    const idx = displayComments.findIndex((c) => c._id === activeCommentId);
    if (idx >= 0) setInternalIndex(idx);
  }, [activeCommentId, displayComments]);

  const currentIndex = Math.min(
    internalIndex,
    Math.max(displayComments.length - 1, 0),
  );
  const comment = displayComments[currentIndex];
  // Lock the counter's index to the total's digit count so stepping
  // 1 -> 10 -> 100 never widens "i/n Comments" and shifts the adjacent arrows.
  const counterDigits = String(displayComments.length).length;

  const riskSection = useMemo(() => {
    if (!comment?.description) return null;
    const parsed = parseCommentSections(comment.description);
    return extractRiskSection(parsed).risk;
  }, [comment?.description]);

  const editCommentMutation = useEditCommentMutation();
  const analytics = useAnalytics("ask_puntt_comment_panel");
  const [liked, setLiked] = useState<boolean | null>(comment?.liked ?? null);

  useEffect(() => {
    setLiked(comment?.liked ?? null);
  }, [comment?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleThumbsUp() {
    if (!ticketId || !comment?._id || editCommentMutation.isPending) return;
    const newLiked = liked === true ? null : true;
    setLiked(newLiked);
    if (newLiked === true) {
      const next = displayComments[currentIndex + 1];
      if (next) onCommentClick?.(next);
    }
    analytics.capture("ask_puntt_feedback", {
      feedback: "comment_thumbs_up",
      comment_id: comment._id,
      ticket_id: ticketId,
    });
    editCommentMutation.mutateAsync({
      ticketId,
      messageId: comment._id,
      payload: { description: comment.description ?? "", liked: newLiked },
    });
  }

  function handleThumbsDown() {
    if (!ticketId || !comment?._id || editCommentMutation.isPending) return;
    const newLiked = liked === false ? null : false;
    setLiked(newLiked);
    if (newLiked === false) {
      const fallback =
        displayComments[currentIndex + 1] ?? displayComments[currentIndex - 1];
      if (fallback) onCommentClick?.(fallback);
    }
    analytics.capture("ask_puntt_feedback", {
      feedback: "comment_thumbs_down",
      comment_id: comment._id,
      ticket_id: ticketId,
    });
    editCommentMutation.mutateAsync({
      ticketId,
      messageId: comment._id,
      payload: { description: comment.description ?? "", liked: newLiked },
    });
  }

  function handleRestoreComment() {
    if (!ticketId || !comment?._id || editCommentMutation.isPending) return;
    setLiked(null);
    analytics.capture("ask_puntt_feedback", {
      feedback: "comment_restore",
      comment_id: comment._id,
      ticket_id: ticketId,
    });
    void editCommentMutation
      .mutateAsync({
        ticketId,
        messageId: comment._id,
        payload: { description: comment.description ?? "", liked: null },
      })
      .then(() => {
        onIncludeRejectedCommentsChange?.(false);
      });
  }

  const isRejected = liked === false;

  const fileCitations = useMemo(() => {
    if (!comment) return [];
    return Array.isArray(comment.citations) ? comment.citations : [];
  }, [comment]);

  const canManageSources = isMeaningfulGigsRole();

  async function saveCommentCitations(citations: TicketMessageCitation[]) {
    if (!ticketId || !comment?._id || editCommentMutation.isPending) return;
    await editCommentMutation.mutateAsync({
      ticketId,
      messageId: comment._id,
      payload: {
        description: comment.description ?? "",
        citations,
      },
    });
  }

  const knowledgeRecordCitationIds = useMemo(() => {
    if (!comment) return [];
    return (Array.isArray(comment.citations) ? comment.citations : [])
      .filter(
        (c): c is { knowledgeDataRecordId: string } =>
          typeof c === "object" &&
          c !== null &&
          "knowledgeDataRecordId" in c &&
          typeof (c as { knowledgeDataRecordId?: unknown })
            .knowledgeDataRecordId === "string",
      )
      .map((c) => c.knowledgeDataRecordId);
  }, [comment]);

  const displayFileCitations = useMemo(
    () => fileCitations.filter((c) => !isKnowledgeDbCitation(c)),
    [fileCitations],
  );

  if (!comment) return null;

  const usePanelCompose = commentComposePlacement === "panel";
  const showPanelComposeForm = usePanelCompose && commentMode;

  const navigate = (dir: 1 | -1) => {
    const nextIdx = currentIndex + dir;
    const next = displayComments[nextIdx];
    if (next) {
      setInternalIndex(nextIdx);
      onCommentClick?.(next);
      if (next.page_num != null) {
        onNavigateToPage?.(next.page_num);
      }
    }
  };

  const citationCount = navigableCitations?.length ?? 0;
  const citationIdx = activeCitationIndex ?? 0;
  const atFirstCitation = citationIdx <= 0;
  const atLastCitation =
    citationCount === 0 || citationIdx >= citationCount - 1;

  const handleNavClick = (dir: 1 | -1) => {
    if (onCommentNavigate) {
      onCommentNavigate(dir);
      return;
    }
    navigate(dir);
  };

  const prevDisabled = onCommentNavigate
    ? currentIndex === 0 && atFirstCitation
    : currentIndex === 0;
  const nextDisabled = onCommentNavigate
    ? currentIndex === displayComments.length - 1 && atLastCitation
    : currentIndex === displayComments.length - 1;

  // Reserve a fixed ch width for "current/total" so 1/9 → 9/9 (and 1/10 →
  // 10/10) never nudges the adjacent prev/next buttons. Digits + slash.
  const counterIndexMinCh = String(displayComments.length).length * 2 + 1;

  const commentNav = (
    <nav
      aria-label={t("assetViewer.commentNavigationAriaLabel")}
      data-testid="comment-panel-nav"
      className={`flex ${PANE_HEADER_HEIGHT_CLASS} ${PANE_HEADER_SIDE_INSET_CLASS} w-full shrink-0 items-center gap-2 overflow-hidden border-b border-puntt-neutral-gray-6`}
    >
      {/* Counter + prev/next/add stay grouped on the left so cycling comments
          keeps the mouse stationary next to the readable counter. Source
          citation is rendered AFTER the arrow cluster (see below) so it can
          appear/disappear without shifting the click targets. */}
      <Text
        size="1"
        weight="medium"
        data-testid="comment-panel-counter"
        className="shrink-0 uppercase tracking-wide text-puntt-neutral-gray-9"
      >
        <span
          data-testid="comment-panel-counter-index"
          className="inline-block tabular-nums"
          style={{ minWidth: `${counterIndexMinCh}ch` }}
        >
          {t("assetViewer.commentCounter", {
            current: currentIndex + 1,
            total: displayComments.length,
          })}
        </span>
        {/* No sheet/cell text beside the counter: a spreadsheet location
            renders as the highlighted cells themselves (review pane for
            comment anchors, context pane for source citations). QA
            2026-07-02 flagged the plain-text location here as confusing. */}
        {comment.cellAnchor == null && comment.videoStart != null && (
          <span
            data-testid="comment-panel-video-anchor-badge"
            className="ml-1 normal-case text-puntt-neutral-gray-8"
          >
            · {formatTimeStamp(comment.videoStart)}
          </span>
        )}
      </Text>
      <Flex gap="1" align="center" className="shrink-0">
        <ChevronNavButton
          direction="left"
          disabled={prevDisabled}
          onClick={() => handleNavClick(-1)}
          aria-label={t("assetViewer.previousCommentAriaLabel")}
          data-testid="comment-panel-prev"
        />
        <ChevronNavButton
          direction="right"
          disabled={nextDisabled}
          onClick={() => handleNavClick(1)}
          aria-label={t("assetViewer.nextCommentAriaLabel")}
          data-testid="comment-panel-next"
        />
        {ticketId && onToggleCommentMode && canvasPinPlacementEnabled && (
          <Tooltip
            content={
              commentMode
                ? t("assetViewer.cancelNewComment")
                : t("assetViewer.addComment")
            }
          >
            <button
              type="button"
              onClick={onToggleCommentMode}
              aria-label={
                commentMode
                  ? t("assetViewer.cancelNewComment")
                  : t("assetViewer.addComment")
              }
              aria-pressed={commentMode}
              data-testid="comment-panel-add"
              className={
                commentMode
                  ? `${chevronNavButtonClassName} border border-puntt-neutral-gray-10 bg-puntt-neutral-gray-3`
                  : chevronNavButtonClassName
              }
            >
              <span className="relative inline-flex size-[14px] items-center justify-center">
                <ChatCircle
                  size={PANE_HEADER_ICON_SIZE}
                  weight={commentMode ? "fill" : "regular"}
                  aria-hidden="true"
                />
                <Plus
                  size={8}
                  weight="bold"
                  aria-hidden="true"
                  data-testid="comment-panel-add-plus"
                  className={
                    commentMode
                      ? "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-base-white"
                      : "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                  }
                />
              </span>
            </button>
          </Tooltip>
        )}
      </Flex>
      {/* Source citation — rendered AFTER the arrow cluster so appearing,
          disappearing, or changing index (1/2 → 2/2) never shifts the
          prev/next/add click targets. Acts as the flex absorber between the
          fixed left cluster and the fixed trailing: min-w-0 + overflow-hidden
          lets it gracefully compress (text clips) before the trailing does, so
          the collapse button stays visible at narrow pane widths. The info is
          also readable in the comment card body below. */}
      {citationCount > 1 ? (
        <Text
          size="1"
          data-testid="comment-panel-source-counter"
          className="min-w-0 overflow-hidden whitespace-nowrap text-puntt-neutral-gray-9"
        >
          {t("assetViewer.sourceCounter")}{" "}
          <span className="tabular-nums">
            {citationIdx + 1}/{citationCount}
          </span>
        </Text>
      ) : null}
      {/* Far-right chrome: rejected toggle (variable width) + collapse slot.
          Kept off the left cluster so it never shifts the cycling arrows.
          Label is "Rejected (N)" / "Hide rejected" — shorter than the old
          "Show rejected (N)" to reduce the trailing's natural width and keep
          the collapse button within the nav at narrow pane widths. */}
      <div
        className="ml-auto flex shrink-0 items-center gap-1"
        data-testid="comment-panel-nav-trailing"
      >
        {rejectedCount > 0 && onIncludeRejectedCommentsChange ? (
          <Button
            size="1"
            variant={includeRejectedComments ? "solid" : "soft"}
            color="gray"
            aria-pressed={includeRejectedComments}
            aria-label={
              includeRejectedComments
                ? t("assetViewer.hideRejectedCommentsAriaLabel", {
                    count: rejectedCount,
                  })
                : t("assetViewer.showRejectedCommentsAriaLabel", {
                    count: rejectedCount,
                  })
            }
            data-testid="comment-panel-show-rejected-toggle"
            onClick={() =>
              onIncludeRejectedCommentsChange(!includeRejectedComments)
            }
            className="shrink-0"
          >
            {includeRejectedComments
              ? t("assetViewer.hideRejected")
              : t("assetViewer.rejectedCount", { count: rejectedCount })}
          </Button>
        ) : null}
        {navTrailing}
      </div>
    </nav>
  );

  return (
    <div
      data-testid="asset-viewer-comment-panel"
      className={
        fillContainer
          ? // Content-sized: parent column applies max-h + overflow. Do not
            // stretch to h-full / flex-1 — that left a blank void under Confirm.
            "flex min-h-0 flex-col bg-base-white"
          : "shrink-0 border-t border-puntt-neutral-gray-6 bg-base-white/90 backdrop-blur-sm"
      }
    >
      {/* Nav header — uses the canonical PaneHeader height (PANE_HEADER_HEIGHT_CLASS)
          so the comment pane's title bar lines up with every sibling Ask Puntt
          pane header (artwork, context, ticket). Do NOT size this to the viewer
          toolbar chrome — a pane header matches other pane headers, not toolbars. */}
      {commentNav}
      {/* Comment card — auto height when docked below the asset; parent
          context column scrolls when fillContainer + max-h cap applies.

          `data-active-comment-id` is the panel's stable identity contract:
          it always reflects the currently-rendered comment._id, so QA can
          assert "selection persisted" without snapshotting the inner
          textContent (which legitimately mutates during AI streaming as
          comments accrue thumbs / citations / minor description edits). */}
      <div
        data-testid="comment-panel-card"
        data-active-comment-id={showPanelComposeForm ? undefined : comment._id}
        className="flex flex-col gap-2 p-4"
      >
        {showPanelComposeForm ? (
          <>
            <Flex gap="2" align="center" className="shrink-0">
              {renderCommentAvatar({
                isAI: false,
                createdBy: { name: authorName, avatar: authorAvatar },
              } as BaseTicketComment)}
              <Text
                size="3"
                weight="medium"
                className="text-puntt-neutral-gray-12"
                data-testid="comment-panel-compose-author"
              >
                {authorName}
              </Text>
            </Flex>
            <TextArea
              data-testid="comment-panel-compose-input"
              placeholder={
                draftPin
                  ? t("assetViewer.addACommentPlaceholder")
                  : t("assetViewer.placePinFirstPlaceholder")
              }
              value={composeText}
              onChange={(e) => setComposeText(e.target.value)}
              disabled={!draftPin || isSubmitting}
              aria-describedby={
                !draftPin ? "comment-panel-place-hint" : undefined
              }
              className="min-h-0 flex-1 resize-none"
              rows={3}
            />
            {!draftPin && (
              <Text
                id="comment-panel-place-hint"
                size="1"
                data-testid="comment-panel-place-hint"
                className="shrink-0 text-puntt-neutral-gray-9"
              >
                {t("assetViewer.clickArtworkToPlaceComment")}
              </Text>
            )}
            {sourcePlacementActive && (
              <Text
                size="1"
                data-testid="comment-panel-source-placement-hint"
                className="shrink-0 text-puntt-accent-11"
              >
                {t("assetViewer.clickContextAssetToPlaceSourcePin")}
              </Text>
            )}
            {canManageSources && onDraftCitationsChange && hasContextAssets ? (
              <CommentSourceAttachment
                citations={draftCitations}
                onCitationsChange={onDraftCitationsChange}
                hasContextAssets={hasContextAssets}
                onStartContextSourcePlacement={onStartContextSourcePlacement}
                disabled={isSubmitting}
              />
            ) : null}
            {submitError && (
              <Text
                size="1"
                role="alert"
                data-testid="comment-panel-submit-error"
                className="text-red-600 shrink-0"
              >
                {submitError}
              </Text>
            )}
            <Button
              data-testid="comment-panel-submit"
              size="2"
              className="w-fit shrink-0"
              disabled={
                !draftPin ||
                !composeText.trim() ||
                isSubmitting ||
                !onSubmitComment
              }
              onClick={() => {
                const text = composeText.trim();
                if (!text || !draftPin || !onSubmitComment) return;
                setSubmitError(null);
                void (async () => {
                  try {
                    await onSubmitComment(
                      text,
                      draftCitations.length > 0 ? draftCitations : undefined,
                    );
                  } catch {
                    setSubmitError(t("assetViewer.couldNotSaveCommentError"));
                  }
                })();
              }}
            >
              {t("assetViewer.addCommentButton")}
            </Button>
          </>
        ) : (
          <>
            <Flex gap="2" align="center" className="w-full min-w-0 shrink-0">
              {comment.isAI
                ? renderReviewCommentAvatar(comment)
                : renderCommentAvatar(comment)}
              <Text
                size="3"
                weight="medium"
                className="min-w-0 truncate text-puntt-neutral-gray-12"
                data-testid="comment-panel-author"
              >
                {commentPanelHeaderLabel(
                  comment,
                  authorName,
                  t("assetViewer.defaultCommentLabel"),
                )}
              </Text>
              {riskSection ? (
                <CommentRiskHeaderBadge body={riskSection.body} />
              ) : null}
            </Flex>

            <CommentSectionBody
              description={comment.description ?? ""}
              commentId={comment._id ?? null}
              clampCommentSections={clampCommentSections}
              slotAfterExpand={
                displayFileCitations.length > 0 ||
                knowledgeRecordCitationIds.length > 0 ? (
                  <Flex direction="column" gap="1">
                    {displayFileCitations.length > 0 ? (
                      <CommentCitationLinks
                        citations={displayFileCitations}
                        onCitationClick={onCitationClick}
                      />
                    ) : null}
                    <CommentPanelKnowledgeRecordCitationLinks
                      recordIds={knowledgeRecordCitationIds}
                      onCitationClick={onCitationClick}
                    />
                  </Flex>
                ) : null
              }
            />
            {!comment.isAI &&
            canManageSources &&
            ticketId &&
            !commentMode &&
            (hasContextAssets || fileCitations.length > 0) ? (
              <CommentSourceAttachment
                citations={fileCitations}
                onCitationsChange={(next) => void saveCommentCitations(next)}
                hasContextAssets={hasContextAssets}
                onStartContextSourcePlacement={
                  onStartContextSourcePlacement
                    ? () =>
                        onStartContextSourcePlacement(
                          (citation) =>
                            void saveCommentCitations([
                              ...fileCitations,
                              citation,
                            ]),
                        )
                    : undefined
                }
                disabled={editCommentMutation.isPending}
                compact
              />
            ) : null}
            {commentMode &&
              !showPanelComposeForm &&
              canManageSources &&
              onDraftCitationsChange &&
              hasContextAssets && (
                <CommentSourceAttachment
                  citations={draftCitations}
                  onCitationsChange={onDraftCitationsChange}
                  hasContextAssets={hasContextAssets}
                  onStartContextSourcePlacement={onStartContextSourcePlacement}
                  disabled={isSubmitting}
                  compact
                />
              )}
            {commentMode &&
              !draftPin &&
              commentComposePlacement === "inline" && (
                <Text
                  id="comment-panel-place-hint"
                  size="1"
                  data-testid="comment-panel-place-hint"
                  className="shrink-0 text-puntt-neutral-gray-9"
                >
                  {t("assetViewer.clickArtworkToPlaceComment")}
                </Text>
              )}
          </>
        )}

        {!showPanelComposeForm && comment.isAI && ticketId && (
          <Flex
            gap="2"
            align="center"
            className="w-full shrink-0"
            data-testid="comment-panel-feedback"
          >
            {isRejected ? (
              <CommentFeedbackActionButton
                data-testid="comment-panel-restore-button"
                label={t("assetViewer.restore")}
                icon={ArrowClockwise}
                pressed={false}
                onClick={handleRestoreComment}
                tone="restore"
                disabled={editCommentMutation.isPending}
                ariaLabel={t("assetViewer.restoreCommentAriaLabel")}
              />
            ) : (
              <>
                <CommentFeedbackActionButton
                  data-testid="comment-panel-thumbs-up"
                  authTrigger="thumbs-up"
                  label={t("assetViewer.confirm")}
                  icon={CheckCircle}
                  pressed={liked === true}
                  onClick={handleThumbsUp}
                  tone="confirm"
                />
                <CommentFeedbackActionButton
                  data-testid="comment-panel-thumbs-down"
                  authTrigger="thumbs-down"
                  label={t("assetViewer.reject")}
                  icon={XCircle}
                  pressed={false}
                  onClick={handleThumbsDown}
                  tone="reject"
                />
              </>
            )}
          </Flex>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UnconvertedOfficeNotice — shown instead of a broken <img> when a Word or
// PowerPoint file reaches the pane before its PDF conversion is available.
// ---------------------------------------------------------------------------

function UnconvertedOfficeNotice({ asset }: { asset: AssetFile }) {
  const { t } = useTranslation("ui");
  const fileName =
    asset.name ?? t("assetViewer.unconvertedOffice.fallbackFileName");
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading || !asset.source) return;
    setDownloading(true);
    try {
      await downloadAsset(asset.source, fileName);
    } catch {
      // downloadAsset already handles user-visible failure paths.
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-2 bg-base-white px-6 text-center"
      data-testid="asset-viewer-unconverted-office"
      aria-label={t("assetViewer.unconvertedOffice.ariaLabel")}
    >
      <Text size="2" weight="bold" className="text-puntt-neutral-gray-12">
        {t("assetViewer.unconvertedOffice.title")}
      </Text>
      <Text as="p" size="2" className="max-w-sm text-puntt-neutral-gray-9">
        {t("assetViewer.unconvertedOffice.body", { fileName })}
      </Text>
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading || !asset.source}
        data-testid="asset-viewer-unconverted-office-download"
        aria-label={t("assetViewer.unconvertedOffice.downloadAriaLabel", {
          fileName,
        })}
        className="inline-flex items-center gap-1 text-sm font-medium text-puntt-accent-11 underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {downloading ? (
          <Spinner size="1" aria-hidden="true" />
        ) : (
          <ArrowSquareOut size={14} aria-hidden="true" />
        )}
        {t("assetViewer.unconvertedOffice.downloadLabel")}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------

function SpreadsheetViewer({
  url,
  comments,
  activeCommentId,
  onCommentClick,
  highlightPosition,
}: {
  url: string;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onCommentClick?: (comment: BaseTicketComment) => void;
  /**
   * Citation target. When `cellAnchor` is set, SpreadsheetView switches to
   * the cited sheet and outlines the cited cells/row while the highlight is
   * active (the context pane uses this for "view source" on spreadsheet
   * context assets, which have no page/pin coordinates).
   */
  highlightPosition?: HighlightPosition | null;
}) {
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setPublicUrl(null);
    getSignedPdfUrl(url)
      .then((u) => {
        if (!cancelled) setPublicUrl(u);
      })
      .catch(() => {
        if (!cancelled) setPublicUrl(url);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  // Jump to the sheet that owns the active comment so selecting a finding in
  // the comment panel surfaces its cell on the right sheet tab. A citation
  // highlight wins over the active comment — clicking "view source" must
  // land on the cited sheet even while a comment elsewhere is selected.
  const highlightAnchor = highlightPosition?.cellAnchor ?? null;
  const activeSheetIndex = useMemo(() => {
    if (highlightAnchor?.sheetIndex != null) return highlightAnchor.sheetIndex;
    if (!activeCommentId) return undefined;
    const active = comments?.find((c) => c._id === activeCommentId);
    return active?.cellAnchor?.sheetIndex;
  }, [highlightAnchor, activeCommentId, comments]);

  return (
    <div data-testid="asset-viewer-spreadsheet" className="size-full">
      {publicUrl == null ? (
        <div
          data-testid="spreadsheet-viewer-loading"
          className="flex size-full items-center justify-center bg-base-white"
        >
          <Spinner size="3" />
        </div>
      ) : (
        <SpreadsheetView
          url={publicUrl}
          comments={comments}
          activeCommentId={activeCommentId}
          activeSheetIndex={activeSheetIndex}
          highlight={highlightAnchor}
          onCommentClick={(commentId) => {
            const match = comments?.find((c) => c._id === commentId);
            if (match) onCommentClick?.(match);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// VideoViewer -- video player with HLS, seek bar, and comment timestamp pins
// ---------------------------------------------------------------------------

function VideoViewer({
  url,
  comments,
  activeCommentId,
  onPinClick,
  reviewStatus,
  highlightPosition,
}: {
  url: string;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onPinClick?: (comment: BaseTicketComment) => void;
  /**
   * Optional processing status forwarded from the parent. When it's set
   * and not "Review complete", we show a lightweight "Preparing video…"
   * placeholder instead of the player because the HLS playlist may not
   * yet be resolvable — the review pipeline is still transcoding.
   */
  reviewStatus?: string | null;
  /**
   * Citation target. When `videoStart` is set, the player seeks to that
   * timestamp and a gold ping marks it on the seek bar for as long as the
   * highlight is active (the context pane uses this for "view source" on
   * video context assets, which have no page/pin coordinates).
   */
  highlightPosition?: HighlightPosition | null;
}) {
  const { t } = useTranslation("ui");
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);

  const stillProcessing =
    reviewStatus != null && reviewStatus !== "Review complete";

  const videoComments = useMemo(
    () => (comments ?? []).filter((c) => c.videoStart != null),
    [comments],
  );
  const visibleVideoComments = useMemo(() => {
    if (!activeCommentId) return videoComments;
    return videoComments.filter((c) => c._id === activeCommentId);
  }, [videoComments, activeCommentId]);

  // Attach hls.js + track duration. Mirrors the ticket's `VideoMode`
  // setup intentionally — that player has been shipping the straight
  // `<video src> + hls.attachMedia` pattern without the failure modes
  // we kept hitting with the custom retry/error hook.
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !url || stillProcessing) return;

    const handleLoadedMetadata = () => {
      if (videoElement.duration && Number.isFinite(videoElement.duration)) {
        setDuration(videoElement.duration);
      }
    };
    videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
    videoElement.addEventListener("durationchange", handleLoadedMetadata);

    let hls: Hls | null = null;
    const isHlsManifest = /\.m3u8(\?|$)/i.test(url);
    if (isHlsManifest && Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(videoElement);
    }

    return () => {
      videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      videoElement.removeEventListener("durationchange", handleLoadedMetadata);
      hls?.destroy();
    };
  }, [url, stillProcessing]);

  // Set initial volume on mount
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Seek to active comment's timestamp
  useEffect(() => {
    if (!activeCommentId || !videoRef.current) return;
    const comment = videoComments.find((c) => c._id === activeCommentId);
    if (comment?.videoStart != null) {
      videoRef.current.currentTime = comment.videoStart;
      videoRef.current.pause();
      setIsPlaying(false);
      setProgress(duration > 0 ? (comment.videoStart / duration) * 100 : 0);
    }
  }, [activeCommentId, videoComments, duration]);

  // Seek to a citation target's timestamp. Re-fires when `duration` lands so
  // a seek requested before metadata loaded still applies. Identity of the
  // highlight object changes per click, so re-clicking the same citation
  // re-seeks even if the user scrubbed away in between.
  useEffect(() => {
    const t = highlightPosition?.videoStart;
    if (t == null || !videoRef.current) return;
    videoRef.current.currentTime = t;
    videoRef.current.pause();
    setIsPlaying(false);
    setProgress(duration > 0 ? (t / duration) * 100 : 0);
  }, [highlightPosition, duration]);

  // Pause on tab switch / visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  // Pause on URL change (asset switch)
  useEffect(() => {
    return () => {
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }
    };
  }, [url]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const pct = (video.currentTime / video.duration) * 100;
    setProgress(pct);
    if (pct >= 100) setIsPlaying(false);
  };

  const handleSeek = (value: number[]) => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const seekTime = (value[0] / 100) * video.duration;
    video.currentTime = seekTime;
    setProgress(value[0]);
  };

  const handleVolumeChange = (value: number[]) => {
    setVolume(value[0]);
    if (videoRef.current) {
      videoRef.current.volume = value[0];
      setIsMuted(value[0] === 0);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  if (stillProcessing) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center gap-3 p-8"
        data-testid="asset-viewer-video-processing"
      >
        <Spinner size="3" />
        <Text size="2" className="text-puntt-neutral-gray-11">
          {t("assetViewer.preparingVideoPreview")}
        </Text>
        <Text size="1" className="text-puntt-neutral-gray-9">
          {reviewStatus}
        </Text>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Video area */}
      <div
        data-testid="asset-viewer-pane-div-615"
        role="button"
        tabIndex={0}
        className="relative flex min-h-0 flex-1 cursor-pointer items-center justify-center bg-puntt-neutral-gray-3"
        onClick={togglePlayPause}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            togglePlayPause();
          }
        }}
      >
        {/* eslint-disable jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          src={url}
          className="max-h-full max-w-full object-contain"
          onTimeUpdate={handleTimeUpdate}
          playsInline
        />
        {!isPlaying && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-puntt-neutral-gray-10/75 p-3">
            <Play className="text-white" size={28} weight="fill" />
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="shrink-0 border-t border-puntt-neutral-gray-6 bg-base-white">
        {/* Seek bar with comment pins */}
        <div className="relative px-3 pt-2">
          <Slider
            variant="classic"
            size="1"
            min={0}
            max={100}
            step={0.1}
            value={[progress]}
            onValueChange={handleSeek}
            className="z-[1] cursor-pointer [--cursor-slider-thumb:pointer] [--radius-factor:0]"
          />
          {/* Comment pins overlay */}
          <div className="pointer-events-none absolute inset-x-3 top-0 h-5">
            {highlightPosition?.videoStart != null && duration > 0 && (
              <div
                data-testid="video-citation-ping"
                role="presentation"
                aria-hidden="true"
                className="absolute top-0 z-10 size-2.5 -translate-x-1/2 scale-150 animate-pulse rounded-full bg-puntt-gold-9 ring-2 ring-puntt-gold-9/40"
                style={{
                  left: `${(highlightPosition.videoStart / duration) * 100}%`,
                }}
              />
            )}
            {visibleVideoComments.map((comment) => {
              const left =
                duration > 0 ? ((comment.videoStart ?? 0) / duration) * 100 : 0;
              return (
                <button
                  data-testid="asset-viewer-pane-button-662"
                  key={comment._id}
                  className="pointer-events-auto absolute top-0 z-10 size-2.5 -translate-x-1/2 scale-150 rounded-full bg-puntt-accent-9 transition-transform"
                  style={{ left: `${left}%` }}
                  title={`${formatTimeStamp(comment.videoStart)} — ${comment.description?.slice(0, 60) ?? ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPinClick?.(comment);
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Transport controls */}
        <Flex align="center" justify="between" className="px-3 py-1.5">
          <Flex align="center" gap="2">
            <IconButton
              data-testid="asset-viewer-pane-icon-button-684"
              size="1"
              variant="ghost"
              color="gray"
              onClick={togglePlayPause}
              aria-label={
                isPlaying ? t("assetViewer.pause") : t("assetViewer.play")
              }
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} />}
            </IconButton>
            <Text size="1" className="tabular-nums text-puntt-neutral-gray-11">
              {formatTimeStamp(videoRef.current?.currentTime ?? 0)} /{" "}
              {formatTimeStamp(duration)}
            </Text>
          </Flex>
          <Flex align="center" gap="2">
            <IconButton
              data-testid="asset-viewer-pane-icon-button-699"
              size="1"
              variant="ghost"
              color="gray"
              onClick={toggleMute}
              aria-label={
                isMuted ? t("assetViewer.unmute") : t("assetViewer.mute")
              }
            >
              {isMuted ? (
                <SpeakerSimpleSlash size={16} />
              ) : (
                <SpeakerSimpleHigh size={16} />
              )}
            </IconButton>
            <Slider
              variant="classic"
              size="1"
              min={0}
              max={1}
              step={0.05}
              value={[isMuted ? 0 : volume]}
              onValueChange={handleVolumeChange}
              className="w-16 cursor-pointer [--cursor-slider-thumb:pointer]"
            />
            <IconButton
              data-testid="asset-viewer-pane-icon-button-722"
              size="1"
              variant="ghost"
              color="gray"
              onClick={toggleFullscreen}
              aria-label={t("assetViewer.fullscreen")}
            >
              <CornersOut size={16} />
            </IconButton>
          </Flex>
        </Flex>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// scrollWrapperToTarget / useScrollToTargetEffect
//
// Keeps the active pin and the citation highlight ping visible inside PdfViewer
// and ImageViewer. Pins and ping share the same positioning scheme
// (rel_x/rel_y of wrapperRef), so one helper handles both. Exported for tests.
// ---------------------------------------------------------------------------

export type ScrollTarget = { relX: number; relY: number };

export function scrollWrapperToTarget(
  scrollEl: HTMLDivElement | null,
  wrapperEl: HTMLDivElement | null,
  target: ScrollTarget | null,
  marginPx = 48,
): void {
  if (!scrollEl || !wrapperEl || !target) return;
  const { offsetWidth: ww, offsetHeight: wh } = wrapperEl;
  if (ww === 0 || wh === 0) return;
  const targetX = wrapperEl.offsetLeft + target.relX * ww;
  const targetY = wrapperEl.offsetTop + target.relY * wh;
  const { clientWidth: cw, clientHeight: ch, scrollLeft, scrollTop } = scrollEl;
  const visibleX =
    targetX >= scrollLeft + marginPx && targetX <= scrollLeft + cw - marginPx;
  const visibleY =
    targetY >= scrollTop + marginPx && targetY <= scrollTop + ch - marginPx;
  if (visibleX && visibleY) return;
  scrollEl.scrollTo({
    left: Math.max(0, targetX - cw / 2),
    top: Math.max(0, targetY - ch / 2),
    behavior: "smooth",
  });
}

/**
 * Frames to keep waiting for the wrapper to be laid out before giving up on an
 * auto-pan. ~10 frames is a few hundred ms — long enough for the first paint to
 * size the canvas/image, short enough that a genuinely zero-size pane stops.
 */
const WRAPPER_MEASURE_MAX_FRAMES = 10;

/**
 * Run `attempt` on the next frame on which the wrapper is actually measured.
 *
 * A single `requestAnimationFrame` is what made the first-open pan
 * intermittent: if the frame landed before the canvas/image had been laid out,
 * `scrollWrapperToTarget` bailed on `offsetWidth === 0` and nothing ever
 * re-triggered it, leaving the viewer parked away from the comment.
 */
function scheduleWhenWrapperMeasured(
  wrapperRef: RefObject<HTMLDivElement | null>,
  attempt: () => void,
): () => void {
  let framesWaited = 0;
  let id = requestAnimationFrame(function tick() {
    const wrapperEl = wrapperRef.current;
    const measured =
      !!wrapperEl && wrapperEl.offsetWidth > 0 && wrapperEl.offsetHeight > 0;
    if (!measured && framesWaited < WRAPPER_MEASURE_MAX_FRAMES) {
      framesWaited += 1;
      id = requestAnimationFrame(tick);
      return;
    }
    attempt();
  });
  return () => cancelAnimationFrame(id);
}

function useScrollToTargetEffect(
  ready: boolean,
  target: ScrollTarget | null,
  scrollRef: RefObject<HTMLDivElement | null>,
  wrapperRef: RefObject<HTMLDivElement | null>,
): void {
  const relX = target?.relX ?? null;
  const relY = target?.relY ?? null;
  useEffect(() => {
    if (!ready || relX == null || relY == null) return;
    return scheduleWhenWrapperMeasured(wrapperRef, () => {
      scrollWrapperToTarget(scrollRef.current, wrapperRef.current, {
        relX,
        relY,
      });
    });
  }, [ready, relX, relY, scrollRef, wrapperRef]);
}

const ZOOM_EPSILON = 0.001;

function useFocusOnActiveBboxEffect(
  ready: boolean,
  activeDisplayedBbox: CommentBbox | null,
  activeCommentId: string | null | undefined,
  userHasAdjustedViewRef: RefObject<boolean>,
  setZoom: (value: number | ((z: number) => number)) => void,
  zoom: number,
  scrollRef: RefObject<HTMLDivElement | null>,
  wrapperRef: RefObject<HTMLDivElement | null>,
): void {
  const lastAutoFocusKeyRef = useRef<string | null>(null);
  const pendingBboxScrollRef = useRef<CommentBbox | null>(null);
  const targetZoomRef = useRef<number | null>(null);

  const focusKey =
    activeCommentId && activeDisplayedBbox
      ? `${activeCommentId}:${activeDisplayedBbox.x}:${activeDisplayedBbox.y}:${activeDisplayedBbox.w}:${activeDisplayedBbox.h}`
      : null;

  useEffect(() => {
    pendingBboxScrollRef.current = null;
    targetZoomRef.current = null;
    lastAutoFocusKeyRef.current = null;
  }, [activeCommentId]);

  useLayoutEffect(() => {
    if (!ready || !activeDisplayedBbox || !focusKey) return;
    if (userHasAdjustedViewRef.current) return;
    if (lastAutoFocusKeyRef.current === focusKey) return;
    if (pendingBboxScrollRef.current != null) return;

    const targetZoom = computeZoomToFitBbox(activeDisplayedBbox);
    targetZoomRef.current = targetZoom;
    pendingBboxScrollRef.current = activeDisplayedBbox;
    if (Math.abs(zoom - targetZoom) > ZOOM_EPSILON) {
      setZoom(targetZoom);
    }
  }, [
    ready,
    focusKey,
    activeDisplayedBbox,
    setZoom,
    userHasAdjustedViewRef,
    zoom,
  ]);

  // Scroll only after auto-zoom has applied and the wrapper has been measured.
  useLayoutEffect(() => {
    const bbox = pendingBboxScrollRef.current;
    const targetZoom = targetZoomRef.current;
    if (!bbox || !ready || !focusKey) return;
    if (targetZoom != null && Math.abs(zoom - targetZoom) > ZOOM_EPSILON) {
      return;
    }

    if (!scrollRef.current) return;

    // Wait for the wrapper to be measured instead of bailing on the first
    // unmeasured frame — that drop is what left the first-open spotlight
    // parked off-target until the user nudged the view.
    return scheduleWhenWrapperMeasured(wrapperRef, () => {
      const scrollEl = scrollRef.current;
      const wrapperEl = wrapperRef.current;
      if (!scrollEl || !wrapperEl) return;
      // Still unmeasured after the retry budget: leave the pending refs set so
      // a later re-render can try again rather than marking this focus done.
      if (wrapperEl.offsetWidth === 0 || wrapperEl.offsetHeight === 0) return;
      scrollWrapperToTarget(scrollEl, wrapperEl, {
        relX: bbox.x + bbox.w / 2,
        relY: bbox.y + bbox.h / 2,
      });
      pendingBboxScrollRef.current = null;
      targetZoomRef.current = null;
      lastAutoFocusKeyRef.current = focusKey;
    });
  }, [ready, zoom, focusKey, scrollRef, wrapperRef]);
}

// ---------------------------------------------------------------------------
// PdfViewer -- full-pane renderer with page nav, highlight overlay, and pins
// ---------------------------------------------------------------------------

function PdfViewer({
  url,
  fileName,
  initialPage,
  highlightPosition,
  onPageChange,
  comments,
  activeCommentId,
  onPinClick,
  onPinDragEnd,
  onArtworkBackgroundClick,
  onPasswordRequired,
  commentMode = false,
  draftPin = null,
  onPlaceDraft,
  clickPlacesDraft = false,
  inlineCompose,
  sourcePlacementMode = false,
  draftSourcePin = null,
  citationFocusPin = null,
  onPlaceSourcePin,
  sourceAssetUrl,
  commentNumberById,
}: {
  url: string;
  fileName: string;
  initialPage?: number;
  highlightPosition?: HighlightPosition | null;
  onPageChange?: (setter: ((page: number) => void) | null) => void;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onPinClick?: (comment: BaseTicketComment) => void;
  onPinDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
  onArtworkBackgroundClick?: () => void;
  onPasswordRequired?: (fileName: string) => void;
  commentMode?: boolean;
  draftPin?: DraftPin | null;
  onPlaceDraft?: (relX: number, relY: number, page: number) => void;
  clickPlacesDraft?: boolean;
  inlineCompose?: InlineComposeViewerProps | null;
  sourcePlacementMode?: boolean;
  draftSourcePin?: DraftPin | null;
  citationFocusPin?: DraftPin | null;
  onPlaceSourcePin?: (pin: ContextSourcePinPayload) => void;
  sourceAssetUrl?: string;
  commentNumberById?: Record<string, number>;
}) {
  const { t } = useTranslation("ui");
  const contentInset = useContext(ViewerContentInsetContext);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(
    initialPage && initialPage > 0 ? initialPage : 1,
  );
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const [totalPages, setTotalPages] = useState(0);
  const [rendering, setRendering] = useState(true);
  const [error, setError] = useState<"render" | "load" | "password" | null>(
    null,
  );
  const [notReady, setNotReady] = useState(false);
  /**
   * True after the interactive→settled first-paint sequence finishes. Bbox
   * auto-zoom, comment pins, and the loading spinner must wait for this: CSS-
   * zooming the soft interactive bitmap (up to 2.5x) reads as a low-quality,
   * super-zoomed flash on open, and pins/spinner should not appear until the
   * sharp settled frame is on canvas.
   */
  const [initialSettleDone, setInitialSettleDone] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotations, setRotations] = useState<Record<number, Rotation>>({});
  const pdfDocRef = useRef<Awaited<
    ReturnType<typeof getDocument>["promise"]
  > | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const renderGenRef = useRef(0);
  /** Reused offscreen canvas for double-buffered pdf.js paints. */
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pageBitmapCacheRef = useRef(new PdfPageBitmapCache());
  /** True once any page has painted successfully — avoids sticky error on zoom-only failure. */
  const hasRenderedRef = useRef(false);
  const totalPagesRef = useRef(0);
  totalPagesRef.current = totalPages;
  /**
   * Page requested while the document was still parsing (`totalPages === 0`,
   * so the range check cannot run yet). The setter is registered on mount,
   * long before pdf.js resolves, so dropping these requests is what made
   * source auto-scroll intermittent.
   */
  const pendingPageJumpRef = useRef<number | null>(null);

  useEffect(() => {
    onPageChange?.((page: number) => {
      if (page < 1) return;
      if (totalPagesRef.current === 0) {
        pendingPageJumpRef.current = page;
        return;
      }
      if (page <= totalPagesRef.current) {
        setCurrentPage(page);
      }
    });
    return () => onPageChange?.(null);
  }, [onPageChange]);

  // Apply a queued jump once pdf.js reports the page count.
  useEffect(() => {
    if (totalPages === 0) return;
    const pending = pendingPageJumpRef.current;
    if (pending == null) return;
    pendingPageJumpRef.current = null;
    if (pending >= 1 && pending <= totalPages) {
      setCurrentPage(pending);
    }
  }, [totalPages]);

  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const rotationsRef = useRef(rotations);
  rotationsRef.current = rotations;

  // Navigation: grab-to-pan, center-preserving zoom, pinch-to-zoom, keyboard
  const [isPanning, setIsPanning] = useState(false);
  const pendingAnchorRef = useRef<{
    fx: number;
    fy: number;
    cursorX?: number;
    cursorY?: number;
  } | null>(null);
  const cachedRectRef = useRef<DOMRect | null>(null);
  const panStartRef = useRef<{
    x: number;
    y: number;
    sl: number;
    st: number;
  } | null>(null);
  const panMovedRef = useRef(false);
  const userHasPannedRef = useRef(false);
  const pendingRotationAnchorRef = useRef<{ x: number; y: number } | null>(
    null,
  );

  const VIEWER_PAN_THRESHOLD = 4;

  function handleZoomChange(
    newZoomOrUpdater: number | ((z: number) => number),
  ) {
    userHasPannedRef.current = true;
    const scrollEl = scrollRef.current;
    const canAnchor = scrollEl && scrollEl.scrollWidth > scrollEl.clientWidth;
    if (canAnchor) {
      pendingAnchorRef.current = {
        fx:
          (scrollEl.scrollLeft + scrollEl.clientWidth / 2) /
          scrollEl.scrollWidth,
        fy:
          (scrollEl.scrollTop + scrollEl.clientHeight / 2) /
          scrollEl.scrollHeight,
      };
    }
    setZoom(newZoomOrUpdater);
  }

  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    const anchor = pendingAnchorRef.current;
    if (!scrollEl || !anchor) return;
    if (anchor.cursorX != null && anchor.cursorY != null) {
      scrollEl.scrollLeft = anchor.fx * scrollEl.scrollWidth - anchor.cursorX;
      scrollEl.scrollTop = anchor.fy * scrollEl.scrollHeight - anchor.cursorY;
    } else {
      scrollEl.scrollLeft =
        anchor.fx * scrollEl.scrollWidth - scrollEl.clientWidth / 2;
      scrollEl.scrollTop =
        anchor.fy * scrollEl.scrollHeight - scrollEl.clientHeight / 2;
    }
    pendingAnchorRef.current = null;
  }, [zoom]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    function onWheel(e: WheelEvent) {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      userHasPannedRef.current = true;
      if (!cachedRectRef.current) {
        cachedRectRef.current = scrollEl!.getBoundingClientRect();
      }
      const rect = cachedRectRef.current;
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const fx = (scrollEl!.scrollLeft + cx) / scrollEl!.scrollWidth;
      const fy = (scrollEl!.scrollTop + cy) / scrollEl!.scrollHeight;
      const factor = e.deltaY < 0 ? 1.05 : 0.95;
      pendingAnchorRef.current = { fx, fy, cursorX: cx, cursorY: cy };
      setZoom((z) => clampZoom(z * factor));
    }

    scrollEl.addEventListener("wheel", onWheel, { passive: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => scrollEl.removeEventListener("wheel", onWheel);
  }, []);

  // Reset the view-adjustment lock when navigating to a different comment or
  // when a Source-citation pin lands: an explicit citation click is a
  // navigation intent, and a sticky pan-lock left the viewer on the cited
  // page with the pin off-viewport (then the transient pin cleared) — reading
  // as "no Source pin" for page-2 spec citations.
  useEffect(() => {
    userHasPannedRef.current = false;
  }, [activeCommentId, citationFocusPin]);

  const placementMode = commentMode || sourcePlacementMode;
  const overlayPin = citationFocusPin ?? draftSourcePin ?? draftPin;
  const resolvedSourceAssetUrl = sourceAssetUrl ?? url;

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0 || e.ctrlKey || e.metaKey) return;
    cachedRectRef.current = null;
    panMovedRef.current = false;
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      sl: scrollRef.current?.scrollLeft ?? 0,
      st: scrollRef.current?.scrollTop ?? 0,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    if (placementMode) return;
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!panStartRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    if (placementMode) {
      if (Math.hypot(dx, dy) >= VIEWER_PAN_THRESHOLD)
        panMovedRef.current = true;
      return;
    }
    if (!scrollRef.current) return;
    if (!panMovedRef.current && Math.hypot(dx, dy) < VIEWER_PAN_THRESHOLD)
      return;
    panMovedRef.current = true;
    if (!isPanning) setIsPanning(true);
    userHasPannedRef.current = true;
    scrollRef.current.scrollLeft = panStartRef.current.sl - dx;
    scrollRef.current.scrollTop = panStartRef.current.st - dy;
  }

  function resetPointerInteraction() {
    setIsPanning(false);
    panStartRef.current = null;
    panMovedRef.current = false;
  }

  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (
      commentMode &&
      panStartRef.current &&
      !panMovedRef.current &&
      wrapperRef.current &&
      onPlaceDraft
    ) {
      const coords = relCoordsFromViewerClick(
        e.clientX,
        e.clientY,
        wrapperRef.current,
        currentPageRotation,
      );
      if (coords) onPlaceDraft(coords.relX, coords.relY, currentPage);
    } else if (
      sourcePlacementMode &&
      panStartRef.current &&
      !panMovedRef.current &&
      wrapperRef.current &&
      onPlaceSourcePin &&
      resolvedSourceAssetUrl
    ) {
      const coords = relCoordsFromViewerClick(
        e.clientX,
        e.clientY,
        wrapperRef.current,
        currentPageRotation,
      );
      if (coords) {
        onPlaceSourcePin({
          relX: coords.relX,
          relY: coords.relY,
          page: currentPage,
          sourceUrl: resolvedSourceAssetUrl,
        });
      }
    } else if (!placementMode && panStartRef.current && !panMovedRef.current) {
      onArtworkBackgroundClick?.();
      if (clickPlacesDraft && wrapperRef.current && onPlaceDraft) {
        const coords = relCoordsFromViewerClick(
          e.clientX,
          e.clientY,
          wrapperRef.current,
          currentPageRotation,
        );
        if (coords) onPlaceDraft(coords.relX, coords.relY, currentPage);
      }
    }
    resetPointerInteraction();
  }

  function handlePointerCancel() {
    resetPointerInteraction();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    const PAN_STEP = 50;
    switch (e.key) {
      case "+":
      case "=":
        e.preventDefault();
        handleZoomChange((z) => clampZoom(z + ZOOM_STEP));
        break;
      case "-":
        e.preventDefault();
        handleZoomChange((z) => clampZoom(z - ZOOM_STEP));
        break;
      case "0":
        e.preventDefault();
        handleZoomChange(1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        scrollEl.scrollLeft -= PAN_STEP;
        userHasPannedRef.current = true;
        break;
      case "ArrowRight":
        e.preventDefault();
        scrollEl.scrollLeft += PAN_STEP;
        userHasPannedRef.current = true;
        break;
      case "ArrowUp":
        e.preventDefault();
        scrollEl.scrollTop -= PAN_STEP;
        userHasPannedRef.current = true;
        break;
      case "ArrowDown":
        e.preventDefault();
        scrollEl.scrollTop += PAN_STEP;
        userHasPannedRef.current = true;
        break;
    }
  }

  function handleRotate() {
    if (activeCommentId) {
      userHasPannedRef.current = false;
    } else {
      const scrollEl = scrollRef.current;
      const wrapperEl = wrapperRef.current;
      if (scrollEl && wrapperEl && wrapperEl.offsetWidth > 0) {
        const viewCenterX = scrollEl.scrollLeft + scrollEl.clientWidth / 2;
        const viewCenterY = scrollEl.scrollTop + scrollEl.clientHeight / 2;
        const normX = Math.max(
          0,
          Math.min(
            1,
            (viewCenterX - wrapperEl.offsetLeft) / wrapperEl.offsetWidth,
          ),
        );
        const normY = Math.max(
          0,
          Math.min(
            1,
            (viewCenterY - wrapperEl.offsetTop) / wrapperEl.offsetHeight,
          ),
        );
        pendingRotationAnchorRef.current = inverseRotatePoint(
          { x: normX, y: normY },
          currentPageRotation,
        );
      }
    }
    setRotations((prev) => ({
      ...prev,
      [currentPage]: nextRotation(prev[currentPage] ?? 0),
    }));
  }

  const lastPdfRenderScaleRef = useRef<number | null>(null);
  const lastZoomForSettleRef = useRef(zoom);
  const lastPdfRenderTriggerRef = useRef<{
    page: number;
    rotation: number;
  } | null>(null);

  const renderPage = useCallback(
    async (
      pageNum: number,
      options?: {
        /** When false, keep the prior page painted and skip the loading overlay (trackpad zoom). */
        showLoadingOverlay?: boolean;
        /** Pixel budget tier — interactive during zoom; settled after idle. */
        quality?: PdfRenderQuality;
        /**
         * When true, skip pdf.js if the resolved scale matches the last paint
         * (CSS zoom already covers the layout change).
         */
        skipIfSameScale?: boolean;
      },
    ) => {
      const pdfDoc = pdfDocRef.current;
      const canvas = canvasRef.current;
      if (!pdfDoc || !canvas) return;

      // PDF.js cannot render two pages into the same canvas at once. Cancel the
      // in-flight render; track generation so a superseded call can no-op cleanly.
      const myGen = ++renderGenRef.current;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          // cancel() may throw if the task already settled; safe to ignore.
        }
        renderTaskRef.current = null;
      }

      if (options?.showLoadingOverlay !== false) {
        setRendering(true);
      } else {
        // Zoom-only refresh keeps the prior frame visible; do not leave
        // `rendering` true if an in-flight page render was superseded.
        setRendering(false);
      }
      try {
        const page = await pdfDoc.getPage(pageNum);
        if (myGen !== renderGenRef.current) return;

        const quality = options?.quality ?? "settled";
        const desiredScale = 1.5 * zoomRef.current;
        const userRotation = rotationsRef.current[pageNum] ?? 0;
        // pdf.js's `rotation` arg REPLACES the page's intrinsic /Rotate rather
        // than adding to it. Passing only the user's manual rotation (default 0)
        // silently un-rotates pages that carry a /Rotate flag — e.g. dieline PDFs
        // exported with a rotated artboard render sideways, which also throws
        // every comment pin into the wrong frame (pins are computed against the
        // backend's upright raster). Compose the page's own rotation with the
        // user's manual delta so /Rotate is always honored and the rotate button
        // still cycles on top of it. Accepts {0,90,180,270} CW; viewport
        // width/height swap for 90/270, mirrored into canvas.width/height below.
        const rotation =
          ((((page.rotate ?? 0) + userRotation) % 360) + 360) % 360;
        const baseViewport = page.getViewport({ scale: 1, rotation });
        const scale = resolvePdfRenderScale(
          baseViewport.width,
          baseViewport.height,
          desiredScale,
          quality,
        );
        if (
          options?.skipIfSameScale &&
          lastPdfRenderScaleRef.current != null &&
          Math.abs(lastPdfRenderScaleRef.current - scale) < 1e-4
        ) {
          setRendering(false);
          return;
        }
        const viewport = page.getViewport({ scale, rotation });

        const cacheKey = { page: pageNum, rotation, scale };
        const cached = pageBitmapCacheRef.current.get(cacheKey);
        if (cached && myGen === renderGenRef.current) {
          blitPdfPageBitmap(canvas, cached);
          renderTaskRef.current = null;
          hasRenderedRef.current = true;
          lastPdfRenderScaleRef.current = scale;
          setRendering(false);
          const rotAnchorCached = pendingRotationAnchorRef.current;
          if (rotAnchorCached) {
            pendingRotationAnchorRef.current = null;
            const scrollEl = scrollRef.current;
            const wrapperEl = wrapperRef.current;
            if (scrollEl && wrapperEl) {
              const r = rotationsRef.current[pageNum] ?? 0;
              const displayPoint = rotatePoint(rotAnchorCached, r as Rotation);
              scrollEl.scrollLeft = Math.max(
                0,
                wrapperEl.offsetLeft +
                  displayPoint.x * wrapperEl.offsetWidth -
                  scrollEl.clientWidth / 2,
              );
              scrollEl.scrollTop = Math.max(
                0,
                wrapperEl.offsetTop +
                  displayPoint.y * wrapperEl.offsetHeight -
                  scrollEl.clientHeight / 2,
              );
            }
          }
          return;
        }

        // Double-buffer the render. Assigning `canvas.width` wipes the visible
        // canvas to transparent, so clearing it up-front and THEN awaiting the
        // async pdf.js render blanks the page to the gray viewer background for
        // the whole render. The spotlight auto-zoom re-renders on every comment
        // navigation (zoom is a render dep), so that blank was visible as a
        // flash each time you stepped between comments. Render into a reused
        // offscreen canvas, then resize + blit the visible canvas in one
        // synchronous step once the pixels are ready.
        if (!offscreenCanvasRef.current) {
          offscreenCanvasRef.current = document.createElement("canvas");
        }
        const offscreen = offscreenCanvasRef.current;
        offscreen.width = viewport.width;
        offscreen.height = viewport.height;

        // Snapshotting for the page bitmap cache calls getImageData often;
        // willReadFrequently must be set on the *first* getContext for this
        // canvas or Chrome keeps warning and uses a slower GPU readback path.
        const offContext = offscreen.getContext("2d", {
          willReadFrequently: true,
        });
        if (!offContext) {
          if (!hasRenderedRef.current) {
            setError("render");
          }
          setRendering(false);
          return;
        }

        const task = page.render({ canvasContext: offContext, viewport });
        renderTaskRef.current = task;
        await task.promise;
        if (myGen === renderGenRef.current) {
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.getContext("2d")?.drawImage?.(offscreen, 0, 0);
          const snap = snapshotPdfPageBitmap(offscreen);
          if (snap) {
            pageBitmapCacheRef.current.set(cacheKey, snap);
          }
          renderTaskRef.current = null;
          hasRenderedRef.current = true;
          lastPdfRenderScaleRef.current = scale;
          setRendering(false);
          // Canvas is now the rotated size — restore any pending rotation anchor
          const rotAnchor = pendingRotationAnchorRef.current;
          if (rotAnchor) {
            pendingRotationAnchorRef.current = null;
            const scrollEl = scrollRef.current;
            const wrapperEl = wrapperRef.current;
            if (scrollEl && wrapperEl) {
              const r = rotationsRef.current[pageNum] ?? 0;
              const displayPoint = rotatePoint(rotAnchor, r as Rotation);
              scrollEl.scrollLeft = Math.max(
                0,
                wrapperEl.offsetLeft +
                  displayPoint.x * wrapperEl.offsetWidth -
                  scrollEl.clientWidth / 2,
              );
              scrollEl.scrollTop = Math.max(
                0,
                wrapperEl.offsetTop +
                  displayPoint.y * wrapperEl.offsetHeight -
                  scrollEl.clientHeight / 2,
              );
            }
          }
        }
      } catch (err) {
        // A superseded render rejects with RenderingCancelledException; expected.
        if (
          (err as { name?: string } | null)?.name ===
          "RenderingCancelledException"
        ) {
          return;
        }
        console.error("Error rendering PDF page:", err);
        if (myGen === renderGenRef.current) {
          if (!hasRenderedRef.current) {
            setError("render");
          }
          setRendering(false);
        }
      }
    },
    [t],
  );

  const loadPdfFromUrl = useCallback(
    async (signedUrl: string, cancelled: () => boolean) => {
      try {
        if (typeof performance !== "undefined" && performance.mark) {
          performance.mark("ask_puntt_pdf_load_start");
        }
        const pdfDoc = await getDocument(signedUrl).promise;
        if (cancelled()) return;
        pdfDocRef.current = pdfDoc;
        setTotalPages(pdfDoc.numPages);
        setNotReady(false);
        // A jump queued while parsing wins over the current page so the first
        // paint is already the requested page instead of page 1 then a jump.
        const requestedPage = pendingPageJumpRef.current;
        pendingPageJumpRef.current = null;
        const startPage = Math.min(
          Math.max(1, requestedPage ?? currentPageRef.current ?? 1),
          pdfDoc.numPages,
        );
        // Claim the page/rotation trigger so the page-change effect does not
        // start a competing settled render (and cancel this first paint).
        lastPdfRenderTriggerRef.current = {
          page: startPage,
          rotation: rotationsRef.current[startPage] ?? 0,
        };
        if (startPage !== currentPageRef.current) {
          setCurrentPage(startPage);
        }
        // Interactive first paint (8MP budget) so large-format pages appear
        // quickly; then upgrade to settled sharpness without blanking.
        await renderPage(startPage, {
          quality: "interactive",
          showLoadingOverlay: true,
        });
        if (cancelled()) return;
        if (typeof performance !== "undefined" && performance.mark) {
          performance.mark("ask_puntt_pdf_first_paint");
        }
        await renderPage(startPage, {
          quality: "settled",
          showLoadingOverlay: false,
          skipIfSameScale: true,
        });
        if (cancelled()) return;
        // Reveal auto-zoom only after settle so CSS never upscales the soft
        // interactive frame (the residual zoomed-in flash after LQIP removal).
        setInitialSettleDone(true);
      } catch (err) {
        if (cancelled()) return;
        console.error("Error loading PDF:", err);
        const isPasswordProtected =
          (err as { name?: string })?.name === "PasswordException";
        if (isPasswordProtected) {
          onPasswordRequired?.(fileName);
        }
        setError(isPasswordProtected ? "password" : "load");
        setRendering(false);
      }
    },
    [fileName, onPasswordRequired, renderPage],
  );

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;

    async function loadPdf() {
      setError(null);
      setNotReady(false);
      setRendering(true);
      setInitialSettleDone(false);
      hasRenderedRef.current = false;
      lastPdfRenderScaleRef.current = null;
      lastPdfRenderTriggerRef.current = null;
      pdfDocRef.current = null;
      pageBitmapCacheRef.current.clear();

      const signed = await getSignedPdfUrlResult(url);
      if (cancelled) return;
      if (signed.status === "not_ready") {
        setNotReady(true);
        setRendering(false);
        return;
      }
      // Presign failure (400/network): fall back to the original static URL so
      // pdf.js can still load via the service worker, matching getSignedPdfUrl.
      const loadUrl = signed.status === "ok" ? signed.url : url;
      await loadPdfFromUrl(loadUrl, isCancelled);
    }

    loadPdf();
    return () => {
      cancelled = true;
      // Bump generation so any renderPage still suspended in getPage() short-
      // circuits after resume instead of touching a detached canvas/state.
      renderGenRef.current++;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          // ignore
        }
        renderTaskRef.current = null;
      }
    };
  }, [url, loadPdfFromUrl]);

  const currentPageRotation = rotations[currentPage] ?? 0;
  // Page / rotation changes need an immediate raster (with overlay). Zoom is
  // CSS-first: the wrapper width updates on every tick, and pdf.js only
  // re-rasters after settle so large-format pages stay painted.
  // Initial open paints via loadPdfFromUrl (interactive → settled); that path
  // seeds lastPdfRenderTriggerRef so this effect does not cancel first paint.
  useEffect(() => {
    if (!pdfDocRef.current || currentPage <= 0) return;
    const prev = lastPdfRenderTriggerRef.current;
    const pageOrRotationChanged =
      prev == null ||
      prev.page !== currentPage ||
      prev.rotation !== currentPageRotation;
    lastPdfRenderTriggerRef.current = {
      page: currentPage,
      rotation: currentPageRotation,
    };
    if (!pageOrRotationChanged) return;
    lastPdfRenderScaleRef.current = null;
    void renderPage(currentPage, {
      showLoadingOverlay: true,
      quality: "settled",
    });
  }, [currentPage, currentPageRotation, renderPage]);

  useEffect(() => {
    if (!pdfDocRef.current || currentPage <= 0) return;
    // Only settle-raster when zoom actually changed. Page/rotation paints
    // are owned by the effect above (and initial load by loadPdfFromUrl).
    if (lastZoomForSettleRef.current === zoom) return;
    lastZoomForSettleRef.current = zoom;
    const timer = window.setTimeout(() => {
      if (typeof performance !== "undefined" && performance.mark) {
        performance.mark("ask_puntt_pdf_rerender_start");
      }
      void renderPage(currentPage, {
        showLoadingOverlay: false,
        quality: "settled",
        skipIfSameScale: true,
      }).finally(() => {
        if (
          typeof performance !== "undefined" &&
          performance.mark &&
          performance.measure
        ) {
          try {
            performance.mark("ask_puntt_pdf_rerender_end");
            performance.measure(
              "ask_puntt_pdf_rerender_ms",
              "ask_puntt_pdf_rerender_start",
              "ask_puntt_pdf_rerender_end",
            );
          } catch {
            // Missing marks (superseded render) — ignore.
          }
        }
      });
    }, PDF_ZOOM_SETTLE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [zoom, currentPage, currentPageRotation, renderPage]);

  useEffect(() => {
    if (highlightPosition?.page && highlightPosition.page !== currentPage) {
      setCurrentPage(highlightPosition.page);
    }
  }, [highlightPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollRef.current.scrollLeft = 0;
    }
  }, [url]);

  const rotatedHighlight = useMemo(
    () =>
      highlightPosition?.x != null && highlightPosition.y != null
        ? rotatePoint(
            { x: highlightPosition.x, y: highlightPosition.y },
            currentPageRotation,
          )
        : null,
    [highlightPosition, currentPageRotation],
  );

  // Center the active pin / citation highlight in view when selection changes.
  // Targets are memoized so the effect only fires when the actual coordinates
  // change, not on every poll that refreshes the `comments` array reference.
  // Gated on `!rendering` so wrapperRef.offsetHeight reflects the new page.
  // Pins and ping render in displayed (rotated) space; apply `rotatePoint` so
  // the scroll lands where the target actually appears on screen.
  const activePinTarget = useMemo<ScrollTarget | null>(() => {
    if (!activeCommentId) return null;
    const pin = (comments ?? []).find((c) => c._id === activeCommentId);
    if (!pin || (pin.page_num ?? 1) !== currentPage) return null;
    if (pin.rel_x == null || pin.rel_y == null) return null;
    const r = rotatePoint({ x: pin.rel_x, y: pin.rel_y }, currentPageRotation);
    return { relX: r.x, relY: r.y };
  }, [activeCommentId, comments, currentPage, currentPageRotation]);

  const highlightTarget = useMemo<ScrollTarget | null>(() => {
    if (!highlightPosition || !rotatedHighlight) return null;
    if (highlightPosition.page && highlightPosition.page !== currentPage)
      return null;
    return { relX: rotatedHighlight.x, relY: rotatedHighlight.y };
  }, [highlightPosition, rotatedHighlight, currentPage]);

  const citationPinTarget = useMemo<ScrollTarget | null>(() => {
    const pin = citationFocusPin ?? draftSourcePin;
    if (!pin || pin.page !== currentPage) return null;
    const rotated = rotatePoint(
      { x: pin.relX, y: pin.relY },
      currentPageRotation,
    );
    return { relX: rotated.x, relY: rotated.y };
  }, [citationFocusPin, draftSourcePin, currentPage, currentPageRotation]);

  const activeDisplayedBbox = useMemo(
    () =>
      getActiveCommentDisplayedBbox(
        comments,
        activeCommentId,
        currentPage,
        currentPageRotation,
      ),
    [comments, activeCommentId, currentPage, currentPageRotation],
  );

  useFocusOnActiveBboxEffect(
    !rendering && initialSettleDone,
    activeDisplayedBbox,
    activeCommentId,
    userHasPannedRef,
    setZoom,
    zoom,
    scrollRef,
    wrapperRef,
  );

  useScrollToTargetEffect(
    !rendering &&
      initialSettleDone &&
      !userHasPannedRef.current &&
      !activeDisplayedBbox,
    activePinTarget,
    scrollRef,
    wrapperRef,
  );
  // When a bbox spotlight owns the view, `useFocusOnActiveBboxEffect` drives
  // the zoom-then-scroll. Gating the citation-highlight scroll on
  // `!activeDisplayedBbox` too stops it from firing a competing smooth scroll
  // (at the pre-zoom scale) that lurches the viewport before the spotlight
  // focus lands. Citation-only highlights with no active bbox still scroll.
  useScrollToTargetEffect(
    !rendering &&
      initialSettleDone &&
      !userHasPannedRef.current &&
      !activeDisplayedBbox,
    citationPinTarget ?? highlightTarget,
    scrollRef,
    wrapperRef,
  );

  const pageComments = useMemo(
    () => canvasCommentsForPage(comments, activeCommentId, currentPage),
    [comments, activeCommentId, currentPage],
  );

  // Surface backend bugs where a comment lands at the renderer with no
  // page_num. The filter above defaults to page 1 — without this warning
  // every orphan pin would silently render on page 1 with no signal that
  // page was unknown. Log once per comment id per session.
  useOrphanPagePinWarning(comments);

  const toolbarPlacement =
    useContext(ViewerToolbarPlacementContext)?.placement ?? "bottom";
  const toolbarReady = !notReady && !error;
  const headerToolbar = useMemo(
    () => (
      <ViewerToolbarHeaderCluster
        currentPage={currentPage}
        totalPages={totalPages}
        zoom={zoom}
        rotation={currentPageRotation}
        onPageChange={setCurrentPage}
        onZoomChange={handleZoomChange}
        onRotate={handleRotate}
      />
    ),
    [
      currentPage,
      totalPages,
      zoom,
      currentPageRotation,
      handleZoomChange,
      handleRotate,
    ],
  );
  useReportedViewerToolbar(
    toolbarReady && toolbarPlacement !== "bottom" ? headerToolbar : null,
    [
      toolbarReady,
      toolbarPlacement,
      currentPage,
      totalPages,
      zoom,
      currentPageRotation,
    ],
  );

  if (notReady) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <StillProcessingFallback
          url={url}
          fileName={fileName}
          onReady={(signedUrl) => {
            setNotReady(false);
            setRendering(true);
            void loadPdfFromUrl(signedUrl, () => false);
          }}
          onGiveUp={() => {
            setNotReady(false);
            setError("load");
          }}
        />
      </div>
    );
  }

  if (error) {
    const isPasswordError = error === "password";
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-xs flex-col items-center gap-3 text-center">
          {isPasswordError ? (
            <Lock size={32} className="text-puntt-neutral-gray-9" />
          ) : null}
          <Text size="2" className="text-puntt-neutral-gray-11" weight="medium">
            {isPasswordError
              ? t("assetViewer.pdfPasswordProtectedTitle")
              : t("assetViewer.failedToLoadPdf")}
          </Text>
          <Text size="1" className="text-puntt-neutral-gray-9">
            {isPasswordError
              ? t("assetViewer.pdfEncryptedDetail")
              : error === "render"
                ? t("assetViewer.failedToRenderPage")
                : t("assetViewer.failedToLoadPdf")}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      <div
        data-testid="asset-viewer-pane-div-1284"
        ref={scrollRef}
        role="application"
        aria-label={t("assetViewer.documentViewerAriaLabel")}
        tabIndex={0}
        className={`relative flex-1 overflow-auto bg-puntt-neutral-gray-3 outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9 ${viewerScrollPadding(contentInset)} ${
          placementMode
            ? "select-none"
            : isPanning
              ? "cursor-grabbing select-none"
              : "cursor-grab"
        }`}
        style={
          sourcePlacementMode
            ? { cursor: SOURCE_PLACEMENT_CURSOR }
            : commentMode
              ? { cursor: COMMENT_MODE_CURSOR }
              : undefined
        }
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onKeyDown={handleKeyDown}
      >
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        {!initialSettleDone && (
          <div
            data-testid="pdf-viewer-loading"
            aria-busy="true"
            aria-label={t("assetViewer.loadingPdfPreview")}
            className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-puntt-neutral-gray-3/80"
          >
            <Spinner size="2" />
          </div>
        )}
        <div
          ref={wrapperRef}
          className="relative mx-auto"
          style={{ width: `${zoom * 100}%` }}
        >
          <canvas ref={canvasRef} className="relative z-0 block w-full" />
          {activeDisplayedBbox && !rendering && initialSettleDone && (
            <SpotlightOverlay bbox={activeDisplayedBbox} />
          )}
          {!rendering &&
            initialSettleDone &&
            pageComments.map((comment) => (
              <ImageCommentPin
                key={comment._id}
                comment={comment}
                displayNumber={commentNumberById?.[comment._id]}
                isActive={activeCommentId === comment._id}
                dimmed={
                  activeCommentId != null && activeCommentId !== comment._id
                }
                showIdleBorder={activeCommentId == null}
                onPinClick={(c) => onPinClick?.(c)}
                onDragEnd={onPinDragEnd}
                containerRef={wrapperRef}
                rotation={currentPageRotation}
              />
            ))}
          {overlayPin?.page === currentPage &&
            !rendering &&
            initialSettleDone && (
              <DraftCommentPin
                relX={overlayPin.relX}
                relY={overlayPin.relY}
                rotation={currentPageRotation}
                data-testid={
                  citationFocusPin
                    ? "comment-citation-focus-pin"
                    : draftSourcePin
                      ? "comment-draft-source-pin"
                      : "comment-draft-pin"
                }
              />
            )}
          {inlineCompose && commentMode && draftPin?.page === currentPage && (
            <CommentComposeBubble
              data-testid="comment-compose-bubble"
              relX={draftPin.relX}
              relY={draftPin.relY}
              rotation={currentPageRotation}
              scrollRef={scrollRef}
              authorName={inlineCompose.authorName}
              authorAvatar={inlineCompose.authorAvatar}
              value={inlineCompose.composeText}
              onChange={inlineCompose.onComposeTextChange}
              onSubmit={inlineCompose.onSubmitInlineComment}
              onCancel={inlineCompose.onCancelCompose}
              isSubmitting={inlineCompose.isSubmitting}
              submitError={inlineCompose.submitError}
            />
          )}
          {initialSettleDone &&
            rotatedHighlight &&
            highlightPosition &&
            (!highlightPosition.page ||
              highlightPosition.page === currentPage) && (
              <div
                className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-full"
                style={{
                  left: toCssPercent(rotatedHighlight.x),
                  top: toCssPercent(rotatedHighlight.y),
                }}
                data-testid="asset-viewer-citation-highlight"
                role="presentation"
                aria-hidden="true"
              >
                {/* Steady, high-contrast ring — stays visible for the
                    full highlight window so the user has time to notice
                    it on a busy spec PDF. Nestle CSV #11 feedback: prior
                    single `animate-ping` element was too brief to spot
                    once it had collapsed back to scale 1 / opacity 0. */}
                <div className="size-12 rounded-full border-[3px] border-puntt-gold-9 bg-puntt-gold-9/25 shadow-[0_0_0_3px_rgba(255,255,255,0.7)]" />
                {/* Layered animated ping on top of the ring for an
                    initial attention-grab; the ring underneath remains
                    after each pulse cycle so the highlight never blinks
                    out between cycles. */}
                <div className="absolute inset-0 size-12 animate-ping rounded-full border-[3px] border-puntt-gold-9 bg-puntt-gold-9/40" />
              </div>
            )}
        </div>
      </div>

      {toolbarPlacement === "bottom" && (
        <ViewerToolbar
          currentPage={currentPage}
          totalPages={totalPages}
          zoom={zoom}
          rotation={currentPageRotation}
          onPageChange={setCurrentPage}
          onZoomChange={handleZoomChange}
          onRotate={handleRotate}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ImageViewer -- image gallery with highlight overlay, comment pins, and zoom
// ---------------------------------------------------------------------------

function ImageViewer({
  assets,
  highlightPosition,
  comments,
  activeCommentId,
  onPinClick,
  onPinDragEnd,
  onArtworkBackgroundClick,
  onPageChange,
  initialPage,
  commentMode = false,
  draftPin = null,
  onPlaceDraft,
  clickPlacesDraft = false,
  inlineCompose,
  sourcePlacementMode = false,
  draftSourcePin = null,
  citationFocusPin = null,
  onPlaceSourcePin,
  sourceAssetUrl,
  commentNumberById,
}: {
  assets: AssetFile[];
  highlightPosition?: HighlightPosition | null;
  comments?: BaseTicketComment[];
  activeCommentId?: string | null;
  onPinClick?: (comment: BaseTicketComment) => void;
  onPinDragEnd?: (
    comment: BaseTicketComment,
    newRelX: number,
    newRelY: number,
  ) => void;
  onArtworkBackgroundClick?: () => void;
  onPageChange?: (setter: ((page: number) => void) | null) => void;
  initialPage?: number;
  commentMode?: boolean;
  draftPin?: DraftPin | null;
  onPlaceDraft?: (relX: number, relY: number, page: number) => void;
  clickPlacesDraft?: boolean;
  inlineCompose?: InlineComposeViewerProps | null;
  sourcePlacementMode?: boolean;
  draftSourcePin?: DraftPin | null;
  citationFocusPin?: DraftPin | null;
  onPlaceSourcePin?: (pin: ContextSourcePinPayload) => void;
  sourceAssetUrl?: string;
  commentNumberById?: Record<string, number>;
}) {
  const { t } = useTranslation("ui");
  const contentInset = useContext(ViewerContentInsetContext);
  // Translate 1-based page → 0-based index. Clamp to assets length so a
  // stale auto-jump request can't index past the array.
  const initialIndex =
    initialPage && initialPage > 0
      ? Math.min(initialPage - 1, Math.max(0, assets.length - 1))
      : 0;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [imgReady, setImgReady] = useState(false);
  const [rotations, setRotations] = useState<Record<number, Rotation>>({});
  const [naturalDims, setNaturalDims] = useState<
    Record<number, { w: number; h: number }>
  >({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const totalPages = assets.length;
  const asset = assets[currentIndex] ?? assets[0];
  const totalPagesRef = useRef(totalPages);
  totalPagesRef.current = totalPages;

  // Navigation: grab-to-pan, center-preserving zoom, pinch-to-zoom, keyboard
  const [isPanning, setIsPanning] = useState(false);
  const pendingAnchorRef = useRef<{
    fx: number;
    fy: number;
    cursorX?: number;
    cursorY?: number;
  } | null>(null);
  const cachedRectRef = useRef<DOMRect | null>(null);
  const panStartRef = useRef<{
    x: number;
    y: number;
    sl: number;
    st: number;
  } | null>(null);
  const panMovedRef = useRef(false);
  const userHasPannedRef = useRef(false);
  const pendingRotationAnchorRef = useRef<{ x: number; y: number } | null>(
    null,
  );

  const VIEWER_PAN_THRESHOLD = 4;

  function handleZoomChange(
    newZoomOrUpdater: number | ((z: number) => number),
  ) {
    userHasPannedRef.current = true;
    const scrollEl = scrollRef.current;
    const canAnchor =
      scrollEl && scrollEl.scrollWidth > scrollEl.clientWidth && imgReady;
    if (canAnchor) {
      pendingAnchorRef.current = {
        fx:
          (scrollEl.scrollLeft + scrollEl.clientWidth / 2) /
          scrollEl.scrollWidth,
        fy:
          (scrollEl.scrollTop + scrollEl.clientHeight / 2) /
          scrollEl.scrollHeight,
      };
    }
    setZoom(newZoomOrUpdater);
  }

  useLayoutEffect(() => {
    const scrollEl = scrollRef.current;
    const anchor = pendingAnchorRef.current;
    if (!scrollEl || !anchor) return;
    if (anchor.cursorX != null && anchor.cursorY != null) {
      scrollEl.scrollLeft = anchor.fx * scrollEl.scrollWidth - anchor.cursorX;
      scrollEl.scrollTop = anchor.fy * scrollEl.scrollHeight - anchor.cursorY;
    } else {
      scrollEl.scrollLeft =
        anchor.fx * scrollEl.scrollWidth - scrollEl.clientWidth / 2;
      scrollEl.scrollTop =
        anchor.fy * scrollEl.scrollHeight - scrollEl.clientHeight / 2;
    }
    pendingAnchorRef.current = null;
  }, [zoom]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    function onWheel(e: WheelEvent) {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      userHasPannedRef.current = true;
      if (!cachedRectRef.current) {
        cachedRectRef.current = scrollEl!.getBoundingClientRect();
      }
      const rect = cachedRectRef.current;
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const fx = (scrollEl!.scrollLeft + cx) / scrollEl!.scrollWidth;
      const fy = (scrollEl!.scrollTop + cy) / scrollEl!.scrollHeight;
      const factor = e.deltaY < 0 ? 1.05 : 0.95;
      pendingAnchorRef.current = { fx, fy, cursorX: cx, cursorY: cy };
      setZoom((z) => clampZoom(z * factor));
    }

    scrollEl.addEventListener("wheel", onWheel, { passive: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => scrollEl.removeEventListener("wheel", onWheel);
  }, []);

  // Reset the view-adjustment lock when navigating to a different comment or
  // when a Source-citation pin lands: an explicit citation click is a
  // navigation intent, and a sticky pan-lock left the viewer on the cited
  // page with the pin off-viewport (then the transient pin cleared) — reading
  // as "no Source pin" for page-2 spec citations.
  useEffect(() => {
    userHasPannedRef.current = false;
  }, [activeCommentId, citationFocusPin]);

  const placementMode = commentMode || sourcePlacementMode;
  const overlayPin = citationFocusPin ?? draftSourcePin ?? draftPin;
  const resolvedSourceAssetUrl = sourceAssetUrl ?? asset?.source;

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0 || e.ctrlKey || e.metaKey) return;
    cachedRectRef.current = null;
    panMovedRef.current = false;
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      sl: scrollRef.current?.scrollLeft ?? 0,
      st: scrollRef.current?.scrollTop ?? 0,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    if (placementMode) return;
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!panStartRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    if (placementMode) {
      if (Math.hypot(dx, dy) >= VIEWER_PAN_THRESHOLD)
        panMovedRef.current = true;
      return;
    }
    if (!scrollRef.current) return;
    if (!panMovedRef.current && Math.hypot(dx, dy) < VIEWER_PAN_THRESHOLD)
      return;
    panMovedRef.current = true;
    if (!isPanning) setIsPanning(true);
    userHasPannedRef.current = true;
    scrollRef.current.scrollLeft = panStartRef.current.sl - dx;
    scrollRef.current.scrollTop = panStartRef.current.st - dy;
  }

  function resetPointerInteraction() {
    setIsPanning(false);
    panStartRef.current = null;
    panMovedRef.current = false;
  }

  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (
      commentMode &&
      panStartRef.current &&
      !panMovedRef.current &&
      wrapperRef.current &&
      onPlaceDraft
    ) {
      const coords = relCoordsFromViewerClick(
        e.clientX,
        e.clientY,
        wrapperRef.current,
        rotation,
      );
      if (coords) onPlaceDraft(coords.relX, coords.relY, currentIndex + 1);
    } else if (
      sourcePlacementMode &&
      panStartRef.current &&
      !panMovedRef.current &&
      wrapperRef.current &&
      onPlaceSourcePin &&
      resolvedSourceAssetUrl
    ) {
      const coords = relCoordsFromViewerClick(
        e.clientX,
        e.clientY,
        wrapperRef.current,
        rotation,
      );
      if (coords) {
        onPlaceSourcePin({
          relX: coords.relX,
          relY: coords.relY,
          page: currentIndex + 1,
          sourceUrl: resolvedSourceAssetUrl,
        });
      }
    } else if (!placementMode && panStartRef.current && !panMovedRef.current) {
      onArtworkBackgroundClick?.();
      if (clickPlacesDraft && wrapperRef.current && onPlaceDraft) {
        const coords = relCoordsFromViewerClick(
          e.clientX,
          e.clientY,
          wrapperRef.current,
          rotation,
        );
        if (coords) onPlaceDraft(coords.relX, coords.relY, currentIndex + 1);
      }
    }
    resetPointerInteraction();
  }

  function handlePointerCancel() {
    resetPointerInteraction();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    const PAN_STEP = 50;
    switch (e.key) {
      case "+":
      case "=":
        e.preventDefault();
        handleZoomChange((z) => clampZoom(z + ZOOM_STEP));
        break;
      case "-":
        e.preventDefault();
        handleZoomChange((z) => clampZoom(z - ZOOM_STEP));
        break;
      case "0":
        e.preventDefault();
        handleZoomChange(1);
        break;
      case "ArrowLeft":
        e.preventDefault();
        scrollEl.scrollLeft -= PAN_STEP;
        userHasPannedRef.current = true;
        break;
      case "ArrowRight":
        e.preventDefault();
        scrollEl.scrollLeft += PAN_STEP;
        userHasPannedRef.current = true;
        break;
      case "ArrowUp":
        e.preventDefault();
        scrollEl.scrollTop -= PAN_STEP;
        userHasPannedRef.current = true;
        break;
      case "ArrowDown":
        e.preventDefault();
        scrollEl.scrollTop += PAN_STEP;
        userHasPannedRef.current = true;
        break;
    }
  }

  useEffect(() => {
    onPageChange?.((page: number) => {
      if (
        page >= 1 &&
        (totalPagesRef.current === 0 || page <= totalPagesRef.current)
      ) {
        setCurrentIndex(page - 1);
      }
    });
    return () => onPageChange?.(null);
  }, [onPageChange]);

  useEffect(() => {
    if (
      highlightPosition?.page &&
      highlightPosition.page !== currentIndex + 1
    ) {
      setCurrentIndex(
        Math.min(Math.max(highlightPosition.page - 1, 0), totalPages - 1),
      );
    }
  }, [highlightPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset scroll only when the *displayed image source* actually changes.
  // Keying on the `assets` array identity (which can change on every poll
  // because TanStack Query returns a new array reference) was causing the
  // viewport to snap back to the top mid-scroll, preventing users from
  // reaching comments and pins below the fold. Do NOT add rotation to this
  // dep list — rotating should preserve scroll position.
  const currentSource = asset?.source;
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollRef.current.scrollLeft = 0;
    }
  }, [currentSource]);

  // Reset the load gate when the displayed image changes. onLoad on the <img>
  // flips it back to true once pixels are available.
  useEffect(() => {
    setImgReady(false);
  }, [currentSource]);

  // Cached images fire their browser `load` event before React attaches the
  // onLoad listener, so onLoad never runs. Check `complete` on mount (and
  // after the source changes) to cover that case. Natural dimensions must
  // land in the same tick as imgReady — spotlight overlays percentage-position
  // against the wrapper aspect ratio derived from those dims.
  useEffect(() => {
    const img = imgRef.current;
    if (!img?.complete || img.naturalWidth <= 0) return;
    setNaturalDims((prev) => ({
      ...prev,
      [currentIndex]: {
        w: img.naturalWidth,
        h: img.naturalHeight,
      },
    }));
    setImgReady(true);
  }, [currentSource, currentIndex]);

  const rotation: Rotation = rotations[currentIndex] ?? 0;
  const dims = naturalDims[currentIndex];
  const imageArtworkReady =
    imgReady && dims != null && dims.w > 0 && dims.h > 0;
  // Wrapper reflects the displayed (post-rotation) aspect so pin overlays
  // percentage-position against the rotated frame.
  const wrapperAspect = dims
    ? rotation % 180 === 0
      ? `${dims.w} / ${dims.h}`
      : `${dims.h} / ${dims.w}`
    : undefined;
  const rotatedHighlight = useMemo(
    () =>
      highlightPosition?.x != null && highlightPosition.y != null
        ? rotatePoint(
            { x: highlightPosition.x, y: highlightPosition.y },
            rotation,
          )
        : null,
    [highlightPosition, rotation],
  );

  // Memoized scroll targets. Pins and ping are positioned in displayed
  // (rotated) space within the wrapper, so we apply `rotatePoint` to land
  // the scroll at the pin's actual on-screen location.
  const activePinTarget = useMemo<ScrollTarget | null>(() => {
    if (!activeCommentId) return null;
    const pin = (comments ?? []).find((c) => c._id === activeCommentId);
    if (!pin || (pin.page_num ?? 1) !== currentIndex + 1) return null;
    if (pin.rel_x == null || pin.rel_y == null) return null;
    const r = rotatePoint({ x: pin.rel_x, y: pin.rel_y }, rotation);
    return { relX: r.x, relY: r.y };
  }, [activeCommentId, comments, currentIndex, rotation]);

  const highlightTarget = useMemo<ScrollTarget | null>(() => {
    if (!highlightPosition || !rotatedHighlight) return null;
    if (highlightPosition.page && highlightPosition.page !== currentIndex + 1)
      return null;
    return { relX: rotatedHighlight.x, relY: rotatedHighlight.y };
  }, [highlightPosition, rotatedHighlight, currentIndex]);

  const citationPinTarget = useMemo<ScrollTarget | null>(() => {
    const pin = citationFocusPin ?? draftSourcePin;
    if (!pin || pin.page !== currentIndex + 1) return null;
    const rotated = rotatePoint({ x: pin.relX, y: pin.relY }, rotation);
    return { relX: rotated.x, relY: rotated.y };
  }, [citationFocusPin, draftSourcePin, currentIndex, rotation]);

  const activeDisplayedBbox = useMemo(
    () =>
      getActiveCommentDisplayedBbox(
        comments,
        activeCommentId,
        currentIndex + 1,
        rotation,
      ),
    [comments, activeCommentId, currentIndex, rotation],
  );

  useFocusOnActiveBboxEffect(
    imageArtworkReady,
    activeDisplayedBbox,
    activeCommentId,
    userHasPannedRef,
    setZoom,
    zoom,
    scrollRef,
    wrapperRef,
  );

  useLayoutEffect(() => {
    const anchor = pendingRotationAnchorRef.current;
    if (!anchor) return;
    pendingRotationAnchorRef.current = null;
    const scrollEl = scrollRef.current;
    const wrapperEl = wrapperRef.current;
    if (!scrollEl || !wrapperEl) return;
    const displayPoint = rotatePoint(anchor, rotation);
    scrollEl.scrollLeft = Math.max(
      0,
      wrapperEl.offsetLeft +
        displayPoint.x * wrapperEl.offsetWidth -
        scrollEl.clientWidth / 2,
    );
    scrollEl.scrollTop = Math.max(
      0,
      wrapperEl.offsetTop +
        displayPoint.y * wrapperEl.offsetHeight -
        scrollEl.clientHeight / 2,
    );
  }, [rotation]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleRotate() {
    if (activeCommentId) {
      userHasPannedRef.current = false;
    } else {
      const scrollEl = scrollRef.current;
      const wrapperEl = wrapperRef.current;
      if (scrollEl && wrapperEl && wrapperEl.offsetWidth > 0) {
        const viewCenterX = scrollEl.scrollLeft + scrollEl.clientWidth / 2;
        const viewCenterY = scrollEl.scrollTop + scrollEl.clientHeight / 2;
        const normX = Math.max(
          0,
          Math.min(
            1,
            (viewCenterX - wrapperEl.offsetLeft) / wrapperEl.offsetWidth,
          ),
        );
        const normY = Math.max(
          0,
          Math.min(
            1,
            (viewCenterY - wrapperEl.offsetTop) / wrapperEl.offsetHeight,
          ),
        );
        pendingRotationAnchorRef.current = inverseRotatePoint(
          { x: normX, y: normY },
          rotation,
        );
      }
    }
    setRotations((prev) => ({
      ...prev,
      [currentIndex]: nextRotation(prev[currentIndex] ?? 0),
    }));
  }

  useScrollToTargetEffect(
    imageArtworkReady && !userHasPannedRef.current && !activeDisplayedBbox,
    activePinTarget,
    scrollRef,
    wrapperRef,
  );
  // See PdfViewer: gate the citation-highlight scroll on `!activeDisplayedBbox`
  // so it doesn't race the spotlight zoom-then-scroll. Citation-only highlights
  // (no active bbox) still scroll.
  useScrollToTargetEffect(
    imageArtworkReady && !userHasPannedRef.current && !activeDisplayedBbox,
    citationPinTarget ?? highlightTarget,
    scrollRef,
    wrapperRef,
  );

  const pageComments = useMemo(
    () => canvasCommentsForPage(comments, activeCommentId, currentIndex + 1),
    [comments, activeCommentId, currentIndex],
  );

  useOrphanPagePinWarning(comments);

  const toolbarPlacement =
    useContext(ViewerToolbarPlacementContext)?.placement ?? "bottom";
  const headerToolbar = useMemo(
    () => (
      <ViewerToolbarHeaderCluster
        currentPage={currentIndex + 1}
        totalPages={totalPages}
        zoom={zoom}
        rotation={rotation}
        onPageChange={(page) => setCurrentIndex(page - 1)}
        onZoomChange={handleZoomChange}
        onRotate={handleRotate}
      />
    ),
    [currentIndex, totalPages, zoom, rotation, handleZoomChange, handleRotate],
  );
  useReportedViewerToolbar(
    toolbarPlacement !== "bottom" ? headerToolbar : null,
    [toolbarPlacement, currentIndex, totalPages, zoom, rotation],
  );

  return (
    <div className="flex h-full flex-col">
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      <div
        data-testid="asset-viewer-pane-div-1725"
        ref={scrollRef}
        role="application"
        aria-label={t("assetViewer.imageViewerAriaLabel")}
        tabIndex={0}
        className={`relative flex-1 overflow-auto bg-puntt-neutral-gray-3 outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9 ${viewerScrollPadding(contentInset)} ${
          placementMode
            ? "select-none"
            : isPanning
              ? "cursor-grabbing select-none"
              : "cursor-grab"
        }`}
        style={
          sourcePlacementMode
            ? { cursor: SOURCE_PLACEMENT_CURSOR }
            : commentMode
              ? { cursor: COMMENT_MODE_CURSOR }
              : undefined
        }
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onKeyDown={handleKeyDown}
      >
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <div
          ref={wrapperRef}
          className="relative mx-auto"
          style={{
            width: `${zoom * 100}%`,
            aspectRatio: wrapperAspect,
          }}
        >
          {/* Thumbnail blur-up: paint a low-res preview under the full
              image while it decodes. Pins stay gated on `imageArtworkReady`
              (full-image natural size) so coordinates never use thumbnail
              aspect. */}
          {asset.thumbnailUrl && !imgReady && (
            <img
              src={asset.thumbnailUrl}
              alt=""
              aria-hidden="true"
              decoding="async"
              data-testid="asset-viewer-thumbnail-preview"
              className="absolute left-1/2 top-1/2 z-0 block scale-105 blur-sm"
              style={{
                width: rotation % 180 === 0 ? "100%" : "auto",
                height: rotation % 180 === 0 ? "auto" : "100%",
                maxWidth: rotation % 180 === 0 ? "100%" : "none",
                maxHeight: "100%",
                transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                transformOrigin: "center",
                objectFit: "contain",
              }}
            />
          )}
          <img
            ref={imgRef}
            src={asset.source}
            alt={asset.name ?? `Image ${currentIndex + 1}`}
            decoding="async"
            data-testid="asset-viewer-full-image"
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img.naturalWidth <= 0 || img.naturalHeight <= 0) return;
              setNaturalDims((prev) => ({
                ...prev,
                [currentIndex]: {
                  w: img.naturalWidth,
                  h: img.naturalHeight,
                },
              }));
              setImgReady(true);
            }}
            className={`absolute left-1/2 top-1/2 z-0 block transition-opacity duration-150 ${
              imgReady ? "opacity-100" : "opacity-0"
            }`}
            style={{
              // At 0°/180° the image fits the wrapper width; at 90°/270° the
              // aspect flip means we fit its long side to the wrapper height
              // instead. transform-origin center keeps it visually anchored.
              width: rotation % 180 === 0 ? "100%" : "auto",
              height: rotation % 180 === 0 ? "auto" : "100%",
              maxWidth: rotation % 180 === 0 ? "100%" : "none",
              maxHeight: "100%",
              transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
              transformOrigin: "center",
              // Ensure image fits within container without cropping
              objectFit: "contain",
            }}
          />
          {activeDisplayedBbox && imageArtworkReady && (
            <SpotlightOverlay bbox={activeDisplayedBbox} />
          )}
          {imageArtworkReady &&
            pageComments.map((comment) => (
              <ImageCommentPin
                key={comment._id}
                comment={comment}
                displayNumber={commentNumberById?.[comment._id]}
                isActive={activeCommentId === comment._id}
                dimmed={
                  activeCommentId != null && activeCommentId !== comment._id
                }
                showIdleBorder={activeCommentId == null}
                onPinClick={(c) => onPinClick?.(c)}
                onDragEnd={onPinDragEnd}
                containerRef={wrapperRef}
                rotation={rotation}
              />
            ))}
          {overlayPin?.page === currentIndex + 1 && (
            <DraftCommentPin
              relX={overlayPin.relX}
              relY={overlayPin.relY}
              rotation={rotation}
              data-testid={
                citationFocusPin
                  ? "comment-citation-focus-pin"
                  : draftSourcePin
                    ? "comment-draft-source-pin"
                    : "comment-draft-pin"
              }
            />
          )}
          {inlineCompose &&
            commentMode &&
            draftPin?.page === currentIndex + 1 && (
              <CommentComposeBubble
                data-testid="comment-compose-bubble"
                relX={draftPin.relX}
                relY={draftPin.relY}
                rotation={rotation}
                scrollRef={scrollRef}
                authorName={inlineCompose.authorName}
                authorAvatar={inlineCompose.authorAvatar}
                value={inlineCompose.composeText}
                onChange={inlineCompose.onComposeTextChange}
                onSubmit={inlineCompose.onSubmitInlineComment}
                onCancel={inlineCompose.onCancelCompose}
                isSubmitting={inlineCompose.isSubmitting}
                submitError={inlineCompose.submitError}
              />
            )}
          {rotatedHighlight &&
            highlightPosition &&
            (!highlightPosition.page ||
              highlightPosition.page === currentIndex + 1) && (
              <div
                className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-full"
                style={{
                  left: toCssPercent(rotatedHighlight.x),
                  top: toCssPercent(rotatedHighlight.y),
                }}
                data-testid="asset-viewer-citation-highlight"
                role="presentation"
                aria-hidden="true"
              >
                {/* Steady, high-contrast ring + animated ping — see the
                    matching PdfViewer block above for rationale (Nestle
                    CSV #11). Same shape so the highlight feels
                    consistent across image and PDF viewers. */}
                <div className="size-12 rounded-full border-[3px] border-puntt-gold-9 bg-puntt-gold-9/25 shadow-[0_0_0_3px_rgba(255,255,255,0.7)]" />
                <div className="absolute inset-0 size-12 animate-ping rounded-full border-[3px] border-puntt-gold-9 bg-puntt-gold-9/40" />
              </div>
            )}
        </div>
      </div>

      {toolbarPlacement === "bottom" && (
        <ViewerToolbar
          currentPage={currentIndex + 1}
          totalPages={totalPages}
          zoom={zoom}
          rotation={rotation}
          onPageChange={(page) => setCurrentIndex(page - 1)}
          onZoomChange={handleZoomChange}
          onRotate={handleRotate}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared toolbar for pagination + zoom (DRYs up PdfViewer / ImageViewer)
// ---------------------------------------------------------------------------

function ViewerPageControls({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation("ui");
  if (totalPages <= 1) return null;
  return (
    <Flex align="center" gap="2">
      <ChevronNavButton
        direction="left"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        data-testid="asset-viewer-prev-page"
        aria-label={t("assetViewer.previousPageAriaLabel")}
      />
      <Text
        size="1"
        className="tabular-nums text-puntt-neutral-gray-11"
        data-testid="asset-viewer-page-indicator"
      >
        {t("assetViewer.pageIndicator", {
          current: currentPage,
          total: totalPages,
        })}
      </Text>
      <ChevronNavButton
        direction="right"
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        data-testid="asset-viewer-next-page"
        aria-label={t("assetViewer.nextPageAriaLabel")}
      />
    </Flex>
  );
}

function ViewerZoomRotateControls({
  zoom,
  rotation,
  onZoomChange,
  onRotate,
}: {
  zoom: number;
  rotation?: Rotation;
  onZoomChange: (zoom: number | ((z: number) => number)) => void;
  onRotate?: () => void;
}) {
  const { t } = useTranslation("ui");
  return (
    <Flex
      align="center"
      gap="1"
      data-testid="asset-viewer-zoom-rotate-controls"
    >
      {onRotate && (
        <Button
          size="1"
          variant="ghost"
          color="gray"
          onClick={onRotate}
          aria-label={t("assetViewer.rotate90AriaLabel")}
          title={t("assetViewer.rotate90Title", { rotation: rotation ?? 0 })}
          data-testid="asset-viewer-rotate"
          className="cursor-pointer"
        >
          <ArrowClockwise size={14} />
          <Text as="span" size="1">
            {t("assetViewer.rotate")}
          </Text>
        </Button>
      )}
      <Flex align="center" gap="0" className="-mx-0.5">
        <IconButton
          size="1"
          variant="ghost"
          color="gray"
          disabled={zoom <= MIN_ZOOM}
          onClick={() => onZoomChange((z: number) => clampZoom(z - ZOOM_STEP))}
          aria-label={t("assetViewer.zoomOutAriaLabel")}
          data-testid="asset-viewer-zoom-out"
          className="!h-7 !w-7 !min-w-7"
        >
          <MagnifyingGlassMinus size={14} />
        </IconButton>
        <Text
          as="span"
          size="1"
          data-testid="asset-viewer-zoom-reset"
          aria-label={t("assetViewer.zoomLevelAriaLabel", {
            percent: Math.round(zoom * 100),
          })}
          className="min-w-11 select-none px-0.5 text-center tabular-nums text-puntt-neutral-gray-11"
        >
          {Math.round(zoom * 100)}%
        </Text>
        <IconButton
          size="1"
          variant="ghost"
          color="gray"
          disabled={zoom >= MAX_ZOOM}
          onClick={() => onZoomChange((z: number) => clampZoom(z + ZOOM_STEP))}
          aria-label={t("assetViewer.zoomInAriaLabel")}
          data-testid="asset-viewer-zoom-in"
          className="!h-7 !w-7 !min-w-7"
        >
          <MagnifyingGlassPlus size={14} />
        </IconButton>
      </Flex>
    </Flex>
  );
}

function ViewerToolbar({
  currentPage,
  totalPages,
  zoom,
  rotation,
  onPageChange,
  onZoomChange,
  onRotate,
}: {
  currentPage: number;
  totalPages: number;
  zoom: number;
  rotation?: Rotation;
  onPageChange: (page: number) => void;
  onZoomChange: (zoom: number | ((z: number) => number)) => void;
  onRotate?: () => void;
}) {
  return (
    <Flex
      align="center"
      justify="between"
      className="h-10 shrink-0 border-t border-puntt-neutral-gray-6 bg-base-white px-3"
      data-testid="asset-viewer-bottom-toolbar"
    >
      <ViewerPageControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
      <ViewerZoomRotateControls
        zoom={zoom}
        rotation={rotation}
        onZoomChange={onZoomChange}
        onRotate={onRotate}
      />
    </Flex>
  );
}

function ViewerToolbarHeaderCluster({
  currentPage,
  totalPages,
  zoom,
  rotation,
  onPageChange,
  onZoomChange,
  onRotate,
}: {
  currentPage: number;
  totalPages: number;
  zoom: number;
  rotation?: Rotation;
  onPageChange: (page: number) => void;
  onZoomChange: (zoom: number | ((z: number) => number)) => void;
  onRotate?: () => void;
}) {
  return (
    <Flex
      align="center"
      gap="3"
      className="shrink-0"
      data-testid="asset-viewer-header-toolbar"
    >
      <ViewerPageControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
      <ViewerZoomRotateControls
        zoom={zoom}
        rotation={rotation}
        onZoomChange={onZoomChange}
        onRotate={onRotate}
      />
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// AssetViewerPane -- main exported component
// ---------------------------------------------------------------------------

const AssetViewerPaneInner = forwardRef<
  AssetViewerPaneHandle,
  AssetViewerPaneProps
>(function AssetViewerPane(
  {
    title,
    assets,
    highlightPosition,
    reviewStatus,
    comments,
    allReviewComments,
    includeRejectedComments = false,
    onIncludeRejectedCommentsChange,
    activeCommentId,
    onCommentClick,
    onPinClick,
    onCitationClick,
    onPinDragEnd,
    onArtworkBackgroundClick,
    onCollapse,
    onBackToProject,
    ticketId,
    onPasswordRequired,
    hideCommentPanel = false,
    hideTitleRow = false,
    viewerToolbarPlacement = "bottom",
    viewerContentInset = "default",
    onHeaderControlsChange,
    commentComposePlacement = "panel",
    onSubmitHumanComment,
    humanCommentPlacementTrigger = "toggle",
    onDetachedCommentPanelProps,
    draftCitations: draftCitationsProp,
    onDraftCitationsChange: onDraftCitationsChangeProp,
    hasContextAssets = false,
    onStartContextSourcePlacement,
    sourcePlacementActive = false,
    sourcePlacementMode = false,
    draftSourcePin = null,
    onPlaceSourcePin,
    onCancelSourcePlacement,
    citationFocusPin = null,
    commentNumberById,
    initialAssetIndex = 0,
    initialViewerPage,
  },
  ref,
) {
  const { t } = useTranslation("ui");
  const { sessionPinOverride, handlePinDragEnd: applySessionPin } =
    useSessionPinOverride(activeCommentId ?? null);

  const pinDisplayComments = useMemo(
    () =>
      applyActiveSessionPin(
        comments,
        activeCommentId ?? null,
        sessionPinOverride,
      ),
    [comments, activeCommentId, sessionPinOverride],
  );

  const handlePinDragEnd = useCallback(
    (comment: BaseTicketComment, newRelX: number, newRelY: number) => {
      applySessionPin(comment, newRelX, newRelY);
      onPinDragEnd?.(comment, newRelX, newRelY);
    },
    [applySessionPin, onPinDragEnd],
  );

  const [activeTab, setActiveTab] = useState(() => {
    if (assets.length === 0) return 0;
    const index = initialAssetIndex ?? 0;
    return Math.min(Math.max(0, index), assets.length - 1);
  });
  const [headerControls, setHeaderControls] = useState<ReactNode | null>(null);

  useEffect(() => {
    // Only `external` placement hands the toolbar to a parent header. `header`
    // keeps `headerControls` local (rendered in this pane's own header below).
    if (viewerToolbarPlacement !== "external") {
      onHeaderControlsChange?.(null);
      return;
    }
    onHeaderControlsChange?.(headerControls);
    return () => onHeaderControlsChange?.(null);
  }, [headerControls, viewerToolbarPlacement, onHeaderControlsChange]);

  const toolbarPlacementContext = useMemo(
    () =>
      viewerToolbarPlacement !== "bottom"
        ? {
            placement: viewerToolbarPlacement,
            setHeaderControls,
          }
        : null,
    [viewerToolbarPlacement],
  );
  // State (not ref) so the navigation effect re-runs when the inner viewer
  // registers its page setter. A ref would silently drop the first navigation
  // request when comments arrive before the PDF has finished loading.
  const [pageNavigator, setPageNavigator] = useState<
    ((page: number) => void) | null
  >(null);
  const pageNavigatorRef = useRef<((page: number) => void) | null>(null);
  pageNavigatorRef.current = pageNavigator;
  // Stable handler so the inner viewer's `useEffect(..., [onPageChange])`
  // doesn't refire every parent render. A fresh inline arrow would loop:
  // effect fires → setPageNavigator → parent re-renders → new prop ref →
  // effect fires again, hitting React's max-update-depth.
  const registerPageNavigator = useCallback(
    (setter: ((page: number) => void) | null) => setPageNavigator(() => setter),
    [],
  );
  // Page to apply on the newly-activated viewer once it mounts. The viewer
  // reads it as `initialPage`; setting it synchronously via a setter after
  // setActiveTab would hit the outgoing viewer (or a not-yet-loaded new one).
  const pendingPageRef = useRef<number | null>(
    initialViewerPage != null && initialViewerPage > 0
      ? initialViewerPage
      : null,
  );
  // Page requested imperatively (comment click, citation, deep link) before the
  // inner viewer registered its page setter. Without this the request is
  // silently dropped and the pane stays on page 1 — the "have to click the
  // source twice" report. Flushed by the effect below when the setter lands.
  const queuedPageNavRef = useRef<number | null>(null);
  const prevActiveTabRef = useRef(activeTab);

  /**
   * Route every page jump through here so a request that arrives before the
   * viewer is ready is queued instead of discarded.
   */
  const requestPageNavigation = useCallback((page: number) => {
    if (!isPositivePageNum(page)) return;
    const navigate = pageNavigatorRef.current;
    if (!navigate) {
      queuedPageNavRef.current = page;
      return;
    }
    navigate(page);
  }, []);

  useEffect(() => {
    if (!pageNavigator) return;
    const queued = queuedPageNavRef.current;
    if (queued == null) return;
    queuedPageNavRef.current = null;
    pageNavigator(queued);
  }, [pageNavigator]);

  const [internalDraftCitations, setInternalDraftCitations] = useState<
    TicketMessageCitation[]
  >([]);
  const draftCitations = draftCitationsProp ?? internalDraftCitations;
  const onDraftCitationsChange =
    onDraftCitationsChangeProp ?? setInternalDraftCitations;

  const [commentMode, setCommentMode] = useState(false);
  const [draftPin, setDraftPin] = useState<DraftPin | null>(null);
  const [inlineComposeText, setInlineComposeText] = useState("");
  const [inlineSubmitError, setInlineSubmitError] = useState<string | null>(
    null,
  );
  const currentUser = useAppSelector((state) => state.auth.value);
  const queryClient = useQueryClient();
  const createCommentMutation = useMutation({ mutationFn: createComment });
  const [customSubmitPending, setCustomSubmitPending] = useState(false);
  const humanCommentSubmitting =
    createCommentMutation.isPending || customSubmitPending;
  // why: `click` mode has no arm button, so the bare click itself must arm
  // comment mode or the inline bubble (gated on commentMode) never renders.
  const clickPlacesDraft =
    humanCommentPlacementTrigger === "click" &&
    (onSubmitHumanComment != null || ticketId != null);

  const clearInlineComposeState = useCallback(() => {
    setInlineComposeText("");
    setInlineSubmitError(null);
  }, []);

  useEffect(() => {
    setCommentMode(false);
    setDraftPin(null);
    clearInlineComposeState();
    onDraftCitationsChange([]);
  }, [ticketId, clearInlineComposeState, onDraftCitationsChange]);

  const cancelCompose = useCallback(() => {
    setDraftPin(null);
    setCommentMode(false);
    clearInlineComposeState();
    onDraftCitationsChange([]);
    onCancelSourcePlacement?.();
  }, [
    clearInlineComposeState,
    onDraftCitationsChange,
    onCancelSourcePlacement,
  ]);

  const toggleCommentMode = useCallback(() => {
    setCommentMode((on) => {
      if (on) {
        setDraftPin(null);
        clearInlineComposeState();
        onCancelSourcePlacement?.();
      }
      return !on;
    });
  }, [clearInlineComposeState, onCancelSourcePlacement]);

  const placeDraft = useCallback(
    (relX: number, relY: number, page: number) => {
      setDraftPin({ relX, relY, page });
      if (clickPlacesDraft) setCommentMode(true);
    },
    [clickPlacesDraft],
  );

  const submitHumanComment = useCallback(
    async (description: string, citations?: TicketMessageCitation[]) => {
      if (!draftPin) return;
      if (onSubmitHumanComment) {
        setCustomSubmitPending(true);
        try {
          await onSubmitHumanComment({
            description,
            relX: draftPin.relX,
            relY: draftPin.relY,
            page: draftPin.page,
            ...(citations?.length ? { citations } : {}),
          });
        } finally {
          setCustomSubmitPending(false);
        }
        cancelCompose();
        return;
      }
      if (!ticketId) return;
      const created = await createCommentMutation.mutateAsync({
        ticketId,
        payload: {
          description,
          rel_x: draftPin.relX,
          rel_y: draftPin.relY,
          page_num: draftPin.page,
          ...(citations?.length ? { citations } : {}),
        },
      });
      cancelCompose();
      await queryClient.invalidateQueries({
        queryKey: ["ticket-view-comments", ticketId],
      });
      onCommentClick?.(
        withHumanCommentAuthor(
          created,
          currentUser,
          t("assetViewer.defaultAuthorName"),
        ),
      );
    },
    [
      draftPin,
      onSubmitHumanComment,
      cancelCompose,
      ticketId,
      createCommentMutation,
      queryClient,
      onCommentClick,
      currentUser,
      t,
    ],
  );

  const submitInlineComment = useCallback(async () => {
    const text = inlineComposeText.trim();
    if (!text || !draftPin) return;
    setInlineSubmitError(null);
    try {
      await submitHumanComment(
        text,
        draftCitations.length > 0 ? draftCitations : undefined,
      );
    } catch {
      setInlineSubmitError(t("assetViewer.couldNotSaveCommentError"));
    }
  }, [inlineComposeText, draftPin, draftCitations, submitHumanComment, t]);

  const inlineComposeProps: InlineComposeViewerProps | null =
    commentComposePlacement === "inline"
      ? {
          commentComposePlacement: "inline",
          composeText: inlineComposeText,
          onComposeTextChange: setInlineComposeText,
          onSubmitInlineComment: submitInlineComment,
          onCancelCompose: cancelCompose,
          isSubmitting: humanCommentSubmitting,
          submitError: inlineSubmitError,
          authorName: currentUser?.name ?? t("assetViewer.defaultAuthorName"),
          authorAvatar: currentUser?.avatar,
        }
      : null;

  const humanCommentViewerProps = {
    commentMode,
    draftPin,
    onPlaceDraft: placeDraft,
    clickPlacesDraft,
    inlineCompose: inlineComposeProps,
  };

  const sourcePlacementViewerProps = {
    sourcePlacementMode,
    draftSourcePin,
    citationFocusPin,
    onPlaceSourcePin,
    sourceAssetUrl: (assets[activeTab] ?? assets[0])?.source,
  };

  // Page to seed into the viewer on its very first mount, derived from the
  // first auto-selected comment. Without this, comments that arrive before
  // the PDF loads lose their navigation request — the effect below fires
  // once with no setter registered, then never re-runs for that activeId.
  const initialActiveCommentPage = useMemo<number | null>(() => {
    if (!activeCommentId || !comments?.length) return null;
    const comment = comments.find((c) => c._id === activeCommentId);
    const page = comment?.page_num;
    return typeof page === "number" && page > 0 ? page : null;
  }, [activeCommentId, comments]);

  useImperativeHandle(ref, () => ({
    navigateToPage(page: number) {
      requestPageNavigation(page);
    },
    navigateToFile(fileIndex: number, pendingPage?: number) {
      if (fileIndex < 0 || fileIndex >= assets.length) return;
      if (fileIndex === activeTab) {
        // Same file — viewer is already mounted; page directly via the
        // live setter.
        if (pendingPage != null) requestPageNavigation(pendingPage);
        return;
      }
      if (pendingPage != null) pendingPageRef.current = pendingPage;
      setActiveTab(fileIndex);
    },
  }));

  // Once the newly-activated viewer has rendered with `initialPage`, clear the
  // pending ref so manual tab clicks don't reuse a stale page.
  useEffect(() => {
    pendingPageRef.current = null;
  }, [activeTab]);

  // Drop queued imperative navigation when switching files — not on the initial
  // mount, or we'd wipe the page setter the inner viewer just registered.
  useEffect(() => {
    if (prevActiveTabRef.current === activeTab) return;
    prevActiveTabRef.current = activeTab;
    queuedPageNavRef.current = null;
    setPageNavigator(null);
  }, [activeTab]);

  // Navigate to the active comment's page. Depends on `pageNavigator` (state)
  // so it re-fires when the inner viewer finishes loading and registers its
  // setter — covering the race where comments arrive before the PDF parses.
  useEffect(() => {
    if (!activeCommentId || !comments?.length || !pageNavigator) return;
    const comment = comments.find((c) => c._id === activeCommentId);
    if (isPositivePageNum(comment?.page_num)) {
      pageNavigator(comment.page_num);
    }
  }, [activeCommentId, comments, pageNavigator]);

  const canvasPinPlacementEnabled = useMemo(() => {
    if (assets.length === 0) return false;
    const asset = assets[activeTab] ?? assets[0];
    return supportsCanvasPinPlacement(asset);
  }, [assets, activeTab]);

  useEffect(() => {
    if (!commentMode || canvasPinPlacementEnabled) return;
    setCommentMode(false);
    setDraftPin(null);
    clearInlineComposeState();
  }, [commentMode, canvasPinPlacementEnabled, clearInlineComposeState]);

  const hasComments = (comments?.length ?? 0) > 0;
  const useDetachedCommentPanel =
    hideCommentPanel && onDetachedCommentPanelProps != null;

  useEffect(() => {
    if (!onDetachedCommentPanelProps) return;
    if (!hideCommentPanel || assets.length === 0) {
      onDetachedCommentPanelProps(null);
      return;
    }
    if (!hasComments) {
      onDetachedCommentPanelProps(null);
      return;
    }
    onDetachedCommentPanelProps({
      comments: comments!,
      allReviewComments: allReviewComments ?? comments,
      includeRejectedComments,
      onIncludeRejectedCommentsChange,
      activeCommentId,
      onCommentClick,
      onCitationClick,
      onNavigateToPage: requestPageNavigation,
      ticketId,
      commentMode,
      draftPin,
      onToggleCommentMode: toggleCommentMode,
      onSubmitComment: submitHumanComment,
      canvasPinPlacementEnabled,
      isSubmitting: humanCommentSubmitting,
      currentUserName: currentUser?.name,
      currentUserAvatar: currentUser?.avatar,
      draftCitations,
      onDraftCitationsChange,
      hasContextAssets,
      onStartContextSourcePlacement,
      sourcePlacementActive,
    });
    return () => onDetachedCommentPanelProps(null);
  }, [
    onDetachedCommentPanelProps,
    hideCommentPanel,
    assets.length,
    hasComments,
    comments,
    allReviewComments,
    includeRejectedComments,
    onIncludeRejectedCommentsChange,
    activeCommentId,
    onCommentClick,
    onCitationClick,
    requestPageNavigation,
    ticketId,
    commentMode,
    draftPin,
    toggleCommentMode,
    submitHumanComment,
    canvasPinPlacementEnabled,
    humanCommentSubmitting,
    currentUser?.name,
    currentUser?.avatar,
    draftCitations,
    onDraftCitationsChange,
    hasContextAssets,
    onStartContextSourcePlacement,
    sourcePlacementActive,
  ]);

  if (assets.length === 0) {
    return (
      <div className="flex h-full items-center justify-center bg-base-white">
        <Text size="2" className="text-puntt-neutral-gray-9">
          {t("assetViewer.noFilesMessage", { title: title.toLowerCase() })}
        </Text>
      </div>
    );
  }

  const currentAsset = assets[activeTab] ?? assets[0];
  const allImages = assets.length > 1 && assets.every((a) => isImage(a));
  const showTabs = assets.length > 1 && !allImages;

  // When this pane is embedded inside a view that already renders its own
  // title chrome (e.g. context column with title=""), skip an empty title
  // row — tabs alone must not leave a blank h-12 strip under the comment.
  const showTitleRow = Boolean(
    !hideTitleRow &&
    (title ||
      onBackToProject ||
      onCollapse ||
      (viewerToolbarPlacement === "header" && headerControls)),
  );
  const showHeader = Boolean(
    showTitleRow ||
    reviewStatus ||
    showTabs ||
    viewerToolbarPlacement === "header",
  );

  const pane = (
    <div
      data-testid="asset-viewer-pane"
      className="flex h-full flex-col bg-base-white"
    >
      {/* Header */}
      {showHeader && (
        <div
          data-testid="asset-viewer-header"
          className={`shrink-0 border-b border-puntt-neutral-gray-6 ${PANE_HEADER_SURFACE_CLASS}`}
        >
          {/* Title row locked to the canonical PaneHeader height so the
              asset viewer's title bar lines up with the other Ask Puntt
              panes. Review status + asset tabs render in rows BELOW the
              fixed-height bar so it never grows when they are present. */}
          {showTitleRow ? (
            <Flex
              align="center"
              gap="2"
              className={`${PANE_HEADER_HEIGHT_CLASS} ${PANE_HEADER_SIDE_INSET_CLASS} w-full`}
              data-testid="asset-viewer-header-row"
            >
              {onBackToProject && (
                <button
                  onClick={onBackToProject}
                  className="flex items-center gap-1 rounded px-1.5 py-0.5 text-puntt-accent-9 transition-colors hover:bg-puntt-accent-2"
                  aria-label={t("assetViewer.backToProjectAriaLabel")}
                  data-testid="back-to-project"
                >
                  <ArrowLeft size={12} weight="bold" />
                  <Text size="1" weight="medium">
                    {t("assetViewer.project")}
                  </Text>
                </button>
              )}
              <Text
                size="1"
                weight="medium"
                className="uppercase tracking-wide text-puntt-neutral-gray-9"
              >
                {title}
              </Text>
              <div
                className="ml-auto flex min-w-0 items-center gap-2"
                data-testid="asset-viewer-internal-header-controls"
              >
                {viewerToolbarPlacement === "header" && headerControls}
                {onCollapse && (
                  <Tooltip
                    content={t("assetViewer.collapsePaneTooltip", { title })}
                  >
                    <PaneHeaderIconButton
                      aria-label={t("assetViewer.collapsePaneTooltip", {
                        title,
                      })}
                      data-testid="asset-viewer-collapse"
                      onClick={onCollapse}
                    >
                      <ArrowsInSimple
                        size={PANE_HEADER_ICON_SIZE}
                        aria-hidden="true"
                      />
                    </PaneHeaderIconButton>
                  </Tooltip>
                )}
              </div>
            </Flex>
          ) : null}
          {reviewStatus && (
            <div className="px-4 pb-2">
              <Text
                size="1"
                className={`italic ${
                  reviewStatus === "Review complete" ||
                  reviewStatus === t("reviewProgress.complete")
                    ? "text-malachite-700"
                    : "animate-pulse text-puntt-accent-9"
                }`}
              >
                {reviewStatus}
              </Text>
            </div>
          )}
          {showTabs && (
            <Flex
              gap="1"
              className={
                showTitleRow || reviewStatus
                  ? "overflow-x-auto px-4 pb-2"
                  : "overflow-x-auto px-4 py-2"
              }
            >
              {assets.map((asset, i) => {
                const label =
                  asset.name ??
                  t("assetViewer.fileFallbackName", { index: i + 1 });
                return (
                  <button
                    key={i}
                    type="button"
                    data-testid="asset-viewer-tab"
                    title={label}
                    aria-label={label}
                    onClick={() => setActiveTab(i)}
                    className={`inline-flex max-w-40 shrink-0 items-center rounded px-2 py-0.5 transition-colors ${
                      i === activeTab
                        ? "bg-puntt-accent-3 text-puntt-accent-11"
                        : "text-puntt-neutral-gray-9 hover:bg-puntt-neutral-gray-3"
                    }`}
                  >
                    <Text
                      as="span"
                      size="1"
                      weight={i === activeTab ? "medium" : undefined}
                      className="block min-w-0 truncate"
                    >
                      {label}
                    </Text>
                  </button>
                );
              })}
            </Flex>
          )}
        </div>
      )}

      {/* Content
       * AGENT CONTRACT: Render chain is ImageViewer | SpreadsheetViewer |
       * PdfViewer | VideoViewer | UnconvertedOfficeNotice | ImageViewer
       * fallback. Do NOT add an OfficeViewer / view.officeapps.live.com
       * branch for pptx/docx/etc — unconverted Office gets the notice.
       * Pins and citations require PdfViewer/ImageViewer/SpreadsheetView
       * (#product 2026-08-04 / PR #4409). Guarded by AssetViewerPane.test.tsx
       * "pin-capable file types" — keep those tests failing if this regresses.
       */}
      <div className="min-h-0 flex-1">
        {allImages ? (
          <ImageViewer
            assets={assets}
            highlightPosition={highlightPosition}
            onPageChange={registerPageNavigator}
            comments={pinDisplayComments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={handlePinDragEnd}
            onArtworkBackgroundClick={onArtworkBackgroundClick}
            initialPage={
              pendingPageRef.current ?? initialActiveCommentPage ?? undefined
            }
            commentNumberById={commentNumberById}
            {...humanCommentViewerProps}
            {...sourcePlacementViewerProps}
          />
        ) : isSpreadsheet(currentAsset) ? (
          <SpreadsheetViewer
            key={currentAsset.source}
            url={currentAsset.source}
            comments={pinDisplayComments}
            activeCommentId={activeCommentId}
            onCommentClick={onPinClick ?? onCommentClick}
            highlightPosition={highlightPosition}
          />
        ) : isPdf(currentAsset) ? (
          <PdfViewer
            key={currentAsset.source}
            url={currentAsset.source}
            fileName={currentAsset.name ?? currentAsset.source}
            initialPage={
              pendingPageRef.current ?? initialActiveCommentPage ?? undefined
            }
            highlightPosition={highlightPosition}
            onPageChange={registerPageNavigator}
            comments={pinDisplayComments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={handlePinDragEnd}
            onArtworkBackgroundClick={onArtworkBackgroundClick}
            onPasswordRequired={onPasswordRequired}
            commentNumberById={commentNumberById}
            {...humanCommentViewerProps}
            {...sourcePlacementViewerProps}
          />
        ) : isRenderedText(currentAsset) ? (
          <PdfViewer
            key={currentAsset.source}
            url={currentAsset.source}
            fileName={currentAsset.name ?? currentAsset.source}
            initialPage={
              pendingPageRef.current ?? initialActiveCommentPage ?? undefined
            }
            highlightPosition={highlightPosition}
            onPageChange={registerPageNavigator}
            comments={pinDisplayComments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={handlePinDragEnd}
            onPasswordRequired={onPasswordRequired}
            {...humanCommentViewerProps}
            {...sourcePlacementViewerProps}
          />
        ) : isImage(currentAsset) ? (
          <ImageViewer
            assets={[currentAsset]}
            highlightPosition={highlightPosition}
            onPageChange={registerPageNavigator}
            comments={pinDisplayComments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={handlePinDragEnd}
            onArtworkBackgroundClick={onArtworkBackgroundClick}
            commentNumberById={commentNumberById}
            {...humanCommentViewerProps}
            {...sourcePlacementViewerProps}
          />
        ) : isVideo(currentAsset) ? (
          <VideoViewer
            url={currentAsset.source}
            comments={pinDisplayComments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            reviewStatus={reviewStatus}
            highlightPosition={highlightPosition}
          />
        ) : isUnconvertedOffice(currentAsset) ? (
          <UnconvertedOfficeNotice asset={currentAsset} />
        ) : (
          <ImageViewer
            assets={[currentAsset]}
            highlightPosition={highlightPosition}
            onPageChange={registerPageNavigator}
            comments={pinDisplayComments}
            activeCommentId={activeCommentId}
            onPinClick={onPinClick}
            onPinDragEnd={handlePinDragEnd}
            onArtworkBackgroundClick={onArtworkBackgroundClick}
            commentNumberById={commentNumberById}
            {...humanCommentViewerProps}
            {...sourcePlacementViewerProps}
          />
        )}
      </div>

      {/* Comment panel */}
      {hasComments && !hideCommentPanel && !useDetachedCommentPanel && (
        <CommentPanel
          comments={comments!}
          allReviewComments={allReviewComments ?? comments}
          includeRejectedComments={includeRejectedComments}
          onIncludeRejectedCommentsChange={onIncludeRejectedCommentsChange}
          activeCommentId={activeCommentId}
          onCommentClick={onCommentClick}
          onCitationClick={onCitationClick}
          onNavigateToPage={requestPageNavigation}
          ticketId={ticketId}
          commentMode={commentMode}
          draftPin={draftPin}
          onToggleCommentMode={toggleCommentMode}
          onSubmitComment={submitHumanComment}
          canvasPinPlacementEnabled={canvasPinPlacementEnabled}
          isSubmitting={humanCommentSubmitting}
          currentUserName={currentUser?.name}
          currentUserAvatar={currentUser?.avatar}
          commentComposePlacement={commentComposePlacement}
          draftCitations={draftCitations}
          onDraftCitationsChange={onDraftCitationsChange}
          hasContextAssets={hasContextAssets}
          onStartContextSourcePlacement={onStartContextSourcePlacement}
          sourcePlacementActive={sourcePlacementActive}
        />
      )}
    </div>
  );

  const wrappedPane = (
    <ViewerContentInsetContext.Provider value={viewerContentInset}>
      {toolbarPlacementContext ? (
        <ViewerToolbarPlacementContext.Provider value={toolbarPlacementContext}>
          {pane}
        </ViewerToolbarPlacementContext.Provider>
      ) : (
        pane
      )}
    </ViewerContentInsetContext.Provider>
  );

  return wrappedPane;
});

// memo so re-renders driven by sibling state (e.g., pane resize ticks in
// chat.tsx) don't cascade into pdfjs / tldraw / image-canvas children when
// nothing the viewer cares about has changed. Parent must pass stable
// callbacks + a stable `comments` array reference for this to bite — see
// the useMemo wrappers around viewComments in chat.tsx.
export const AssetViewerPane = memo(AssetViewerPaneInner);
